import {
  AlertTriangle,
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  ArrowUpDown,
  Building2,
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Globe,
  Info,
  Loader2,
  Mail,
  Maximize2,
  MoreVertical,
  Pin,
  RotateCcw,
  Save,
  Search,
  Settings,
  Star,
  StarOff,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AddressesTab } from '@/components/customers/addresses-tab';
import { AgeingBarChart } from '@/components/customers/ageing-bar-chart';
import { AllContactsTab } from '@/components/customers/all-contacts-tab';
import { AssignAccountManagerDialog } from '@/components/customers/assign-account-manager-dialog';
import { AssignCreditControllerDialog } from '@/components/customers/assign-credit-controller-dialog';
import { AssignRiskLevelDialog } from '@/components/customers/assign-risk-level-dialog';
import { AssignStrategyDialog } from '@/components/customers/assign-strategy-dialog';
import { CommunicationHistoryTab } from '@/components/customers/communication-history-tab';
import { AddNoteModal, CreateDisputeModal } from '@/components/customers/customer-action-modals';
import { CustomerGuidanceModal, hasSeenGuidance } from '@/components/customers/customer-guidance-modal';
import { CustomerSelectionToolbar } from '@/components/customers/customer-selection-toolbar';
import { CustomerSendEmailModal } from '@/components/customers/customer-send-email-modal';
import { SendStatementsFlow, type StatementCustomer } from '@/components/customers/send-statements-flow';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpSupportButton } from '@/components/help-support';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { type CurrencyMode, CurrencyToggle } from '@/components/shared/currency-toggle';
import { CustomerSearchMultiSelect } from '@/components/shared/customer-search-multi-select';
import { TEMPLATE_VARIABLES } from '@/components/template-variables-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { FILTER_NONE } from '@/constants/filters';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/hooks/use-auth';
import { useAgeingBands } from '@/hooks/useAgeingBands';
import { useBulkUpdateCustomers } from '@/hooks/useBulkUpdateCustomers';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { useCustomerInvoiceAttachments } from '@/hooks/useCustomerInvoiceAttachments';
import { useAddCustomerNote } from '@/hooks/useCustomerNotes';
import {
  buildGraphQLFilterVariable,
  type GetCustomersGraphQLParams,
  sortFieldMap,
  useCustomersPageDataGraphQL,
} from '@/hooks/useCustomersGraphQL';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useExportAll } from '@/hooks/useExportAll';
import { filtersMatch, useFilterPreferences } from '@/hooks/useFilterPreferences';
import { useGeneralSettings } from '@/hooks/useGeneralSettings';
import { useLogCustomerEmail } from '@/hooks/useLogCustomerEmail';
import { useLogCustomerPhoneCall } from '@/hooks/useLogCustomerPhoneCall';
import { useRiskLevels } from '@/hooks/useRiskLevelsData';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useSessionSortPreference } from '@/hooks/useSessionSortPreference';
import { useStrategiesList } from '@/hooks/useStrategiesData';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { contactDisplayName } from '@/lib/contact-display-name';
import { getPageNumbers } from '@/lib/pagination-utils';
import { isRangeInvalid } from '@/lib/range-filter-validation';
import { getRiskBadgeClassName, getRiskLabel, type RiskLevelCode } from '@/lib/risk-level';
import { getPinnedColumnStyle, useColumnWidthObserver } from '@/lib/table-pinning';
import { clipCell } from '@/lib/table-cell-clip';
import { cn, formatCreditLimit, formatCurrency, formatDate, formatDateDDMMYYYY } from '@/lib/utils';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import type { SavedFilterPreset } from '@/types/transaction';
import type { CustomerAgeingBandUI } from '@/types/ui/customer-detail';

// Build a lookup from variable key to panel category for badge coloring
const varCategoryLookup = new Map<string, string>();
for (const v of TEMPLATE_VARIABLES) {
  varCategoryLookup.set(v.variable, v.category);
}

// Coerce a range filter value (which legacy saved presets may store as a string) to number | null
const coerceRangeValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Multi-select values use FILTER_NONE as the sentinel for a null (blank) match.
const toNullableNumberId = (value: string): number | null => (value === FILTER_NONE ? null : Number(value));
const toNullableCode = (value: string): string | null => (value === FILTER_NONE ? null : value);

// Helper function to render text with variable pills
const renderTextWithVariables = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  const variableRegex = /<<([^>]+)>>/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = variableRegex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Determine badge color based on variable category from TEMPLATE_VARIABLES
    const variable = match[0];
    const variant: 'default' | 'secondary' | 'outline' | 'accent' = 'accent';
    const category = varCategoryLookup.get(variable);
    let accent:
      | 'destructive'
      | 'success'
      | 'warning'
      | 'info'
      | 'teal'
      | 'purple'
      | 'nile'
      | 'cerulean'
      | 'lagoon'
      | 'eagle'
      | 'sap'
      | 'orange'
      | 'brick'
      | 'red'
      | 'violet'
      | 'blue-violet' = 'blue-violet';

    switch (category) {
      case 'Customer Information':
        accent = 'blue-violet';
        break;
      case 'Transaction Details':
        accent = 'teal';
        break;
      case 'Company Information':
        accent = 'purple';
        break;
      case 'Payment Information':
        accent = 'orange';
        break;
      case 'System Information':
        accent = 'lagoon';
        break;
      default:
        accent = 'blue-violet';
        break;
    }

    // Add the variable as a badge
    parts.push(
      <Badge key={key++} variant={variant} accent={accent} className="text-[10px] font-mono mx-0.5 inline-flex">
        {variable}
      </Badge>,
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

// Types
interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  outstanding: number;
  overdue: number;
  outstandingHome?: number;
  overdueHome?: number;
  outstandingCustomer?: number;
  overdueCustomer?: number;
  displayOutstanding?: number;
  displayOverdue?: number;
  creditLimit: number;
  currencyCreditLimit?: number;
  dso: number;
  risk: string;
  riskLevelName?: string;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  primaryAddressLine?: string;
  primaryAddressTown?: string;
  primaryAddressCounty?: string;
  primaryAddressPostCode?: string;
  primaryAddressCountry?: string;
  statementAddressLine?: string;
  statementAddressTown?: string;
  statementAddressCounty?: string;
  statementAddressPostCode?: string;
  statementAddressCountry?: string;
  paymentTerms: string;
  collectionStrategyId: string;
  collectionStrategyName?: string;
  creditControllerId?: string;
  creditControllerName?: string;
  accountManagerId?: string;
  accountManagerName?: string;
  otherCompaniesCount?: number;
  userSortKey1?: string;
  userSortKey2?: string;
  userSortKey3?: string;
  userSortKey4?: string;
  userSortKey1Label?: string;
  userSortKey2Label?: string;
  userSortKey3Label?: string;
  userSortKey4Label?: string;
  isAnyCurrency?: boolean;
  transactionCurrencies?: string[];
  status?: string;
  onStop?: boolean;
}

interface Invoice {
  id: string;
  date: string;
  dueDate: string;
  amount: number;
  status: 'overdue' | 'outstanding' | 'paid';
}

interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Data - 56 Customers (kept for potential future use)
const initialCustomers: Customer[] = [
  // High Risk Customers (12)
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    currency: 'GBP',
    outstanding: 214000,
    overdue: 214000,
    creditLimit: 250000,
    dso: 95,
    risk: 'high',
    lastPayment: '2023-11-15',
    contact: 'john.doe@acme.com',
    phone: '+44 20 7946 0301',
    address: '123 Business St, New York, NY 10001',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  {
    id: '2',
    name: 'Global Tech Industries',
    accountNumber: 'ACC-002',
    currency: 'GBP',
    outstanding: 198000,
    overdue: 198000,
    creditLimit: 300000,
    dso: 102,
    risk: 'high',
    lastPayment: '2023-10-20',
    contact: 'sarah.smith@globaltech.com',
    phone: '+44 20 7946 0302',
    address: '456 Tech Ave, San Francisco, CA 94102',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  {
    id: '3',
    name: 'Mega Retail Group',
    accountNumber: 'ACC-003',
    currency: 'EUR',
    outstanding: 187000,
    overdue: 187000,
    creditLimit: 200000,
    dso: 88,
    risk: 'high',
    lastPayment: '2023-12-01',
    contact: 'michael.brown@megaretail.eu',
    phone: '+44 20 7123 4567',
    address: '789 Retail Rd, London, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '3',
  },
  {
    id: '4',
    name: 'Premier Manufacturing',
    accountNumber: 'ACC-004',
    currency: 'GBP',
    outstanding: 176000,
    overdue: 176000,
    creditLimit: 220000,
    dso: 91,
    risk: 'high',
    lastPayment: '2023-11-28',
    contact: 'lisa.johnson@premier.com',
    phone: '+44 121 496 0303',
    address: '321 Factory Ln, Detroit, MI 48201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '5',
    name: 'Sunrise Enterprises',
    accountNumber: 'ACC-005',
    currency: 'GBP',
    outstanding: 165000,
    overdue: 165000,
    creditLimit: 180000,
    dso: 97,
    risk: 'high',
    lastPayment: '2023-11-10',
    contact: 'david.wilson@sunrise.co.uk',
    phone: '+44 20 8123 4567',
    address: '654 Enterprise Way, Manchester, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  {
    id: '6',
    name: 'Quantum Solutions',
    accountNumber: 'ACC-006',
    currency: 'GBP',
    outstanding: 154000,
    overdue: 154000,
    creditLimit: 200000,
    dso: 85,
    risk: 'high',
    lastPayment: '2023-12-05',
    contact: 'emily.davis@quantum.com',
    phone: '+44 161 850 0304',
    address: '987 Innovation Dr, Austin, TX 78701',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '7',
    name: 'Atlantic Trading Co',
    accountNumber: 'ACC-007',
    currency: 'EUR',
    outstanding: 143000,
    overdue: 143000,
    creditLimit: 160000,
    dso: 93,
    risk: 'high',
    lastPayment: '2023-11-18',
    contact: 'robert.miller@atlantic.eu',
    phone: '+33 1 42 12 34 56',
    address: '234 Commerce Blvd, Paris, France',
    paymentTerms: 'Net 45',
    collectionStrategyId: '',
  },
  {
    id: '8',
    name: 'Pacific Imports',
    accountNumber: 'ACC-008',
    currency: 'AUD',
    outstanding: 132000,
    overdue: 132000,
    creditLimit: 150000,
    dso: 89,
    risk: 'high',
    lastPayment: '2023-11-25',
    contact: 'jennifer.taylor@pacific.au',
    phone: '+61 2 9123 4567',
    address: '567 Harbor St, Sydney, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '9',
    name: 'Northern Logistics',
    accountNumber: 'ACC-009',
    currency: 'CAD',
    outstanding: 121000,
    overdue: 121000,
    creditLimit: 140000,
    dso: 86,
    risk: 'high',
    lastPayment: '2023-12-02',
    contact: 'william.anderson@northern.ca',
    phone: '+1 (416) 123-4567',
    address: '890 Transport Ave, Toronto, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  {
    id: '10',
    name: 'Summit Distribution',
    accountNumber: 'ACC-010',
    currency: 'GBP',
    outstanding: 110000,
    overdue: 110000,
    creditLimit: 130000,
    dso: 92,
    risk: 'high',
    lastPayment: '2023-11-22',
    contact: 'mary.thomas@summit.com',
    phone: '+44 113 496 0305',
    address: '123 Distribution Way, Denver, CO 80202',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '11',
    name: 'Coastal Ventures',
    accountNumber: 'ACC-011',
    currency: 'GBP',
    outstanding: 99000,
    overdue: 99000,
    creditLimit: 120000,
    dso: 84,
    risk: 'high',
    lastPayment: '2023-12-08',
    contact: 'james.jackson@coastal.co.uk',
    phone: '+44 20 9123 4567',
    address: '456 Seaside Rd, Brighton, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '12',
    name: 'Metro Services Group',
    accountNumber: 'ACC-012',
    currency: 'GBP',
    outstanding: 88000,
    overdue: 88000,
    creditLimit: 110000,
    dso: 87,
    risk: 'high',
    lastPayment: '2023-11-30',
    contact: 'patricia.white@metro.com',
    phone: '+44 117 929 0306',
    address: '789 Metro Plaza, Chicago, IL 60601',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  // Medium Risk Customers (20)
  {
    id: '13',
    name: 'Horizon Technologies',
    accountNumber: 'ACC-013',
    currency: 'GBP',
    outstanding: 145000,
    overdue: 72500,
    creditLimit: 200000,
    dso: 65,
    risk: 'medium',
    lastPayment: '2024-01-10',
    contact: 'christopher.harris@horizon.com',
    phone: '+44 113 378 0307',
    address: '321 Tech Park, Seattle, WA 98101',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '14',
    name: 'Silverline Industries',
    accountNumber: 'ACC-014',
    currency: 'EUR',
    outstanding: 138000,
    overdue: 69000,
    creditLimit: 180000,
    dso: 62,
    risk: 'medium',
    lastPayment: '2024-01-15',
    contact: 'barbara.martin@silverline.eu',
    phone: '+49 30 1234 5678',
    address: '654 Industrial St, Berlin, Germany',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '15',
    name: 'Apex Commercial',
    accountNumber: 'ACC-015',
    currency: 'GBP',
    outstanding: 127000,
    overdue: 63500,
    creditLimit: 170000,
    dso: 58,
    risk: 'medium',
    lastPayment: '2024-01-20',
    contact: 'daniel.thompson@apex.com',
    phone: '+44 116 318 0308',
    address: '987 Commerce Dr, Boston, MA 02101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '16',
    name: 'Pinnacle Systems',
    accountNumber: 'ACC-016',
    currency: 'GBP',
    outstanding: 116000,
    overdue: 58000,
    creditLimit: 150000,
    dso: 55,
    risk: 'medium',
    lastPayment: '2024-01-25',
    contact: 'nancy.garcia@pinnacle.co.uk',
    phone: '+44 20 1234 5678',
    address: '234 Systems Ave, Birmingham, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '17',
    name: 'Vertex Corporation',
    accountNumber: 'ACC-017',
    currency: 'GBP',
    outstanding: 105000,
    overdue: 52500,
    creditLimit: 140000,
    dso: 52,
    risk: 'medium',
    lastPayment: '2024-02-01',
    contact: 'matthew.martinez@vertex.com',
    phone: '+44 191 605 0309',
    address: '567 Corporate Blvd, Atlanta, GA 30301',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '18',
    name: 'Fusion Enterprises',
    accountNumber: 'ACC-018',
    currency: 'CAD',
    outstanding: 94000,
    overdue: 47000,
    creditLimit: 130000,
    dso: 49,
    risk: 'medium',
    lastPayment: '2024-02-05',
    contact: 'karen.robinson@fusion.ca',
    phone: '+1 (604) 123-4567',
    address: '890 Enterprise Way, Vancouver, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '19',
    name: 'Nexus Trading',
    accountNumber: 'ACC-019',
    currency: 'EUR',
    outstanding: 83000,
    overdue: 41500,
    creditLimit: 120000,
    dso: 46,
    risk: 'medium',
    lastPayment: '2024-02-10',
    contact: 'steven.clark@nexus.eu',
    phone: '+31 20 123 4567',
    address: '123 Trade Center, Amsterdam, Netherlands',
    paymentTerms: 'Net 45',
    collectionStrategyId: '',
  },
  {
    id: '20',
    name: 'Catalyst Solutions',
    accountNumber: 'ACC-020',
    currency: 'GBP',
    outstanding: 72000,
    overdue: 36000,
    creditLimit: 110000,
    dso: 43,
    risk: 'medium',
    lastPayment: '2024-02-15',
    contact: 'betty.rodriguez@catalyst.com',
    phone: '+44 114 273 0310',
    address: '456 Solution St, Phoenix, AZ 85001',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '21',
    name: 'Zenith Group',
    accountNumber: 'ACC-021',
    currency: 'AUD',
    outstanding: 61000,
    overdue: 30500,
    creditLimit: 100000,
    dso: 40,
    risk: 'medium',
    lastPayment: '2024-02-20',
    contact: 'donald.lewis@zenith.au',
    phone: '+61 3 9123 4567',
    address: '789 Business Park, Melbourne, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '22',
    name: 'Vanguard Industries',
    accountNumber: 'ACC-022',
    currency: 'GBP',
    outstanding: 125000,
    overdue: 62500,
    creditLimit: 160000,
    dso: 67,
    risk: 'medium',
    lastPayment: '2024-01-12',
    contact: 'dorothy.lee@vanguard.com',
    phone: '+44 20 7946 0301',
    address: '321 Industrial Pkwy, Pittsburgh, PA 15201',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '23',
    name: 'Omega Systems',
    accountNumber: 'ACC-023',
    currency: 'GBP',
    outstanding: 114000,
    overdue: 57000,
    creditLimit: 150000,
    dso: 64,
    risk: 'medium',
    lastPayment: '2024-01-18',
    contact: 'joshua.walker@omega.co.uk',
    phone: '+44 20 2345 6789',
    address: '654 Tech Quarter, Leeds, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '24',
    name: 'Titan Commercial',
    accountNumber: 'ACC-024',
    currency: 'EUR',
    outstanding: 103000,
    overdue: 51500,
    creditLimit: 140000,
    dso: 61,
    risk: 'medium',
    lastPayment: '2024-01-22',
    contact: 'sandra.hall@titan.eu',
    phone: '+34 91 123 4567',
    address: '987 Commercial Ave, Madrid, Spain',
    paymentTerms: 'Net 45',
    collectionStrategyId: '5',
  },
  {
    id: '25',
    name: 'Eclipse Trading',
    accountNumber: 'ACC-025',
    currency: 'GBP',
    outstanding: 92000,
    overdue: 46000,
    creditLimit: 130000,
    dso: 58,
    risk: 'medium',
    lastPayment: '2024-01-28',
    contact: 'ashley.allen@eclipse.com',
    phone: '+44 20 7946 0302',
    address: '234 Trade Plaza, Miami, FL 33101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '26',
    name: 'Meridian Group',
    accountNumber: 'ACC-026',
    currency: 'CAD',
    outstanding: 81000,
    overdue: 40500,
    creditLimit: 120000,
    dso: 55,
    risk: 'medium',
    lastPayment: '2024-02-03',
    contact: 'kimberly.young@meridian.ca',
    phone: '+1 (403) 123-4567',
    address: '567 Group Center, Calgary, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '27',
    name: 'Spectrum Solutions',
    accountNumber: 'ACC-027',
    currency: 'GBP',
    outstanding: 70000,
    overdue: 35000,
    creditLimit: 110000,
    dso: 52,
    risk: 'medium',
    lastPayment: '2024-02-08',
    contact: 'mark.hernandez@spectrum.com',
    phone: '+44 121 496 0303',
    address: '890 Solution Way, Portland, OR 97201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '28',
    name: 'Prism Enterprises',
    accountNumber: 'ACC-028',
    currency: 'GBP',
    outstanding: 118000,
    overdue: 59000,
    creditLimit: 155000,
    dso: 69,
    risk: 'medium',
    lastPayment: '2024-01-08',
    contact: 'donna.king@prism.co.uk',
    phone: '+44 20 3456 7890',
    address: '123 Enterprise Rd, Glasgow, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '29',
    name: 'Cascade Industries',
    accountNumber: 'ACC-029',
    currency: 'EUR',
    outstanding: 107000,
    overdue: 53500,
    creditLimit: 145000,
    dso: 66,
    risk: 'medium',
    lastPayment: '2024-01-14',
    contact: 'paul.wright@cascade.eu',
    phone: '+41 22 123 4567',
    address: '456 Industrial Zone, Geneva, Switzerland',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '30',
    name: 'Stellar Corporation',
    accountNumber: 'ACC-030',
    currency: 'GBP',
    outstanding: 96000,
    overdue: 48000,
    creditLimit: 135000,
    dso: 63,
    risk: 'medium',
    lastPayment: '2024-01-19',
    contact: 'carol.lopez@stellar.com',
    phone: '+44 161 850 0304',
    address: '789 Corporate Center, Dallas, TX 75201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '31',
    name: 'Quantum Dynamics',
    accountNumber: 'ACC-031',
    currency: 'AUD',
    outstanding: 85000,
    overdue: 42500,
    creditLimit: 125000,
    dso: 60,
    risk: 'medium',
    lastPayment: '2024-01-24',
    contact: 'george.hill@quantum.au',
    phone: '+61 7 9123 4567',
    address: '321 Dynamics Dr, Brisbane, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '32',
    name: 'Nexus Ventures',
    accountNumber: 'ACC-032',
    currency: 'GBP',
    outstanding: 74000,
    overdue: 37000,
    creditLimit: 115000,
    dso: 57,
    risk: 'medium',
    lastPayment: '2024-01-30',
    contact: 'ruth.scott@nexusv.com',
    phone: '+44 113 496 0305',
    address: '654 Venture Blvd, San Diego, CA 92101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  // Low Risk Customers (24)
  {
    id: '33',
    name: 'Bright Future Inc',
    accountNumber: 'ACC-033',
    currency: 'GBP',
    outstanding: 89000,
    overdue: 0,
    creditLimit: 150000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'frank.green@brightfuture.com',
    phone: '+44 117 929 0306',
    address: '987 Future Ave, Minneapolis, MN 55401',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '34',
    name: 'Swift Solutions',
    accountNumber: 'ACC-034',
    currency: 'EUR',
    outstanding: 78000,
    overdue: 0,
    creditLimit: 130000,
    dso: 25,
    risk: 'low',
    lastPayment: '2024-03-05',
    contact: 'virginia.adams@swift.eu',
    phone: '+32 2 123 4567',
    address: '234 Solution St, Brussels, Belgium',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '35',
    name: 'Reliable Trading Co',
    accountNumber: 'ACC-035',
    currency: 'GBP',
    outstanding: 67000,
    overdue: 0,
    creditLimit: 120000,
    dso: 22,
    risk: 'low',
    lastPayment: '2024-03-08',
    contact: 'henry.baker@reliable.co.uk',
    phone: '+44 20 4567 8901',
    address: '567 Trade Center, Edinburgh, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '36',
    name: 'Premier Partners',
    accountNumber: 'ACC-036',
    currency: 'GBP',
    outstanding: 56000,
    overdue: 0,
    creditLimit: 100000,
    dso: 19,
    risk: 'low',
    lastPayment: '2024-03-10',
    contact: 'frances.gonzalez@premier.com',
    phone: '+44 113 378 0307',
    address: '890 Partner Plaza, Nashville, TN 37201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '37',
    name: 'Excellence Corp',
    accountNumber: 'ACC-037',
    currency: 'CAD',
    outstanding: 95000,
    overdue: 4750,
    creditLimit: 140000,
    dso: 32,
    risk: 'low',
    lastPayment: '2024-02-28',
    contact: 'roy.nelson@excellence.ca',
    phone: '+1 (514) 123-4567',
    address: '123 Excellence Way, Montreal, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '38',
    name: 'Integrity Systems',
    accountNumber: 'ACC-038',
    currency: 'GBP',
    outstanding: 84000,
    overdue: 4200,
    creditLimit: 130000,
    dso: 29,
    risk: 'low',
    lastPayment: '2024-03-02',
    contact: 'irene.carter@integrity.com',
    phone: '+44 116 318 0308',
    address: '456 Integrity Dr, Columbus, OH 43201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '39',
    name: 'Quality Enterprises',
    accountNumber: 'ACC-039',
    currency: 'EUR',
    outstanding: 73000,
    overdue: 3650,
    creditLimit: 120000,
    dso: 26,
    risk: 'low',
    lastPayment: '2024-03-04',
    contact: 'randy.mitchell@quality.eu',
    phone: '+43 1 123 4567',
    address: '789 Quality Blvd, Vienna, Austria',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '40',
    name: 'Trustworthy Trading',
    accountNumber: 'ACC-040',
    currency: 'GBP',
    outstanding: 62000,
    overdue: 3100,
    creditLimit: 110000,
    dso: 23,
    risk: 'low',
    lastPayment: '2024-03-06',
    contact: 'marilyn.perez@trustworthy.co.uk',
    phone: '+44 20 5678 9012',
    address: '321 Trust Ave, Cardiff, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '41',
    name: 'Precision Industries',
    accountNumber: 'ACC-041',
    currency: 'GBP',
    outstanding: 91000,
    overdue: 0,
    creditLimit: 135000,
    dso: 30,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'arthur.roberts@precision.com',
    phone: '+44 191 605 0309',
    address: '654 Precision Way, Indianapolis, IN 46201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '42',
    name: 'Efficient Solutions',
    accountNumber: 'ACC-042',
    currency: 'AUD',
    outstanding: 80000,
    overdue: 0,
    creditLimit: 125000,
    dso: 27,
    risk: 'low',
    lastPayment: '2024-03-03',
    contact: 'evelyn.turner@efficient.au',
    phone: '+61 8 9123 4567',
    address: '987 Efficiency Dr, Perth, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '43',
    name: 'Dependable Corp',
    accountNumber: 'ACC-043',
    currency: 'GBP',
    outstanding: 69000,
    overdue: 0,
    creditLimit: 115000,
    dso: 24,
    risk: 'low',
    lastPayment: '2024-03-07',
    contact: 'harold.phillips@dependable.com',
    phone: '+44 114 273 0310',
    address: '234 Dependable St, Charlotte, NC 28201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '44',
    name: 'Solid Ventures',
    accountNumber: 'ACC-044',
    currency: 'CAD',
    outstanding: 58000,
    overdue: 0,
    creditLimit: 105000,
    dso: 21,
    risk: 'low',
    lastPayment: '2024-03-09',
    contact: 'mildred.campbell@solid.ca',
    phone: '+1 (780) 123-4567',
    address: '567 Venture Park, Edmonton, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '45',
    name: 'Stable Systems',
    accountNumber: 'ACC-045',
    currency: 'EUR',
    outstanding: 87000,
    overdue: 4350,
    creditLimit: 130000,
    dso: 31,
    risk: 'low',
    lastPayment: '2024-02-29',
    contact: 'clarence.parker@stable.eu',
    phone: '+45 33 12 34 56',
    address: '890 Stable Way, Copenhagen, Denmark',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '46',
    name: 'Consistent Trading',
    accountNumber: 'ACC-046',
    currency: 'GBP',
    outstanding: 76000,
    overdue: 3800,
    creditLimit: 120000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-02',
    contact: 'carl.evans@consistent.co.uk',
    phone: '+44 20 6789 0123',
    address: '123 Consistent Rd, Liverpool, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '47',
    name: 'Steady Enterprises',
    accountNumber: 'ACC-047',
    currency: 'GBP',
    outstanding: 65000,
    overdue: 3250,
    creditLimit: 110000,
    dso: 25,
    risk: 'low',
    lastPayment: '2024-03-05',
    contact: 'willie.edwards@steady.com',
    phone: '+44 20 7946 0301',
    address: '456 Steady Ave, Milwaukee, WI 53201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '48',
    name: 'Secure Solutions',
    accountNumber: 'ACC-048',
    currency: 'GBP',
    outstanding: 54000,
    overdue: 0,
    creditLimit: 100000,
    dso: 18,
    risk: 'low',
    lastPayment: '2024-03-11',
    contact: 'lois.collins@secure.com',
    phone: '+44 20 7946 0302',
    address: '789 Secure Blvd, Las Vegas, NV 89101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '49',
    name: 'Prompt Partners',
    accountNumber: 'ACC-049',
    currency: 'EUR',
    outstanding: 93000,
    overdue: 0,
    creditLimit: 135000,
    dso: 33,
    risk: 'low',
    lastPayment: '2024-02-27',
    contact: 'earl.stewart@prompt.eu',
    phone: '+46 8 123 4567',
    address: '321 Prompt Plaza, Stockholm, Sweden',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '50',
    name: 'Swift Industries',
    accountNumber: 'ACC-050',
    currency: 'AUD',
    outstanding: 82000,
    overdue: 0,
    creditLimit: 125000,
    dso: 30,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'judith.sanchez@swifti.au',
    phone: '+61 2 8123 4567',
    address: '654 Swift Way, Adelaide, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '51',
    name: 'Rapid Response Co',
    accountNumber: 'ACC-051',
    currency: 'GBP',
    outstanding: 71000,
    overdue: 0,
    creditLimit: 115000,
    dso: 27,
    risk: 'low',
    lastPayment: '2024-03-04',
    contact: 'frederick.morris@rapid.com',
    phone: '+44 121 496 0303',
    address: '987 Response Rd, Baltimore, MD 21201',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '52',
    name: 'Timely Trading',
    accountNumber: 'ACC-052',
    currency: 'CAD',
    outstanding: 60000,
    overdue: 0,
    creditLimit: 105000,
    dso: 24,
    risk: 'low',
    lastPayment: '2024-03-07',
    contact: 'joyce.rogers@timely.ca',
    phone: '+1 (204) 123-4567',
    address: '234 Timely Center, Winnipeg, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '53',
    name: 'Punctual Systems',
    accountNumber: 'ACC-053',
    currency: 'GBP',
    outstanding: 88000,
    overdue: 4400,
    creditLimit: 130000,
    dso: 34,
    risk: 'low',
    lastPayment: '2024-02-26',
    contact: 'gerald.reed@punctual.co.uk',
    phone: '+44 20 7890 1234',
    address: '567 Punctual Ave, Bristol, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '54',
    name: 'Expedite Enterprises',
    accountNumber: 'ACC-054',
    currency: 'EUR',
    outstanding: 77000,
    overdue: 3850,
    creditLimit: 120000,
    dso: 31,
    risk: 'low',
    lastPayment: '2024-02-29',
    contact: 'alice.cook@expedite.eu',
    phone: '+47 22 12 34 56',
    address: '890 Expedite Way, Oslo, Norway',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '55',
    name: 'Agile Corporation',
    accountNumber: 'ACC-055',
    currency: 'GBP',
    outstanding: 66000,
    overdue: 3300,
    creditLimit: 110000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-03',
    contact: 'teresa.morgan@agile.com',
    phone: '+44 161 850 0304',
    address: '123 Agile Blvd, Sacramento, CA 95814',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '56',
    name: 'Dynamic Solutions',
    accountNumber: 'ACC-056',
    currency: 'GBP',
    outstanding: 55000,
    overdue: 0,
    creditLimit: 100000,
    dso: 20,
    risk: 'low',
    lastPayment: '2024-03-10',
    contact: 'joe.bell@dynamic.com',
    phone: '+44 113 496 0305',
    address: '456 Dynamic Dr, Kansas City, MO 64101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
];

// Mock Invoices
const mockInvoices: Record<string, Invoice[]> = {
  '1': [
    {
      id: 'INV-001',
      date: '2023-11-01',
      dueDate: '2023-12-01',
      amount: 76000,
      status: 'overdue',
    },
    {
      id: 'INV-002',
      date: '2023-11-15',
      dueDate: '2023-12-15',
      amount: 68000,
      status: 'overdue',
    },
    {
      id: 'INV-003',
      date: '2023-12-01',
      dueDate: '2023-12-31',
      amount: 70000,
      status: 'overdue',
    },
  ],
};

// Mock Payments
const mockPayments: Record<string, Payment[]> = {
  '1': [
    {
      id: 'PAY-001',
      date: '2023-11-15',
      amount: 50000,
      method: 'Wire Transfer',
      reference: 'WT-2023-001',
      allocations: [
        { invoiceId: 'INV-001', invoiceDate: '2023-10-01', amount: 30000 },
        { invoiceId: 'INV-002', invoiceDate: '2023-10-15', amount: 20000 },
      ],
    },
    {
      id: 'PAY-002',
      date: '2023-10-20',
      amount: 35000,
      method: 'ACH',
      reference: 'ACH-2023-002',
      allocations: [{ invoiceId: 'INV-003', invoiceDate: '2023-09-01', amount: 35000 }],
    },
  ],
};

// Collection Strategies
const collectionStrategies = [
  { id: '1', name: 'Standard Collection Flow', status: 'active' },
  { id: '2', name: 'Intensive Collection', status: 'active' },
  { id: '3', name: 'Gentle Reminder Flow', status: 'inactive' },
];

// Mark mock data as intentionally kept for future use
void initialCustomers;

const DEFAULT_CUSTOMER_COLUMNS: ColumnDefinition[] = [
  { id: 'accountNumber', label: 'Account #', visible: true, order: 0 },
  { id: 'name', label: 'Name', visible: true, order: 1 },
  { id: 'currency', label: 'Currency', visible: true, order: 2 },
  { id: 'outstanding', label: 'Outstanding', visible: true, order: 3 },
  { id: 'overdue', label: 'Overdue', visible: true, order: 4 },
  { id: 'creditLimit', label: 'Credit Limit', visible: true, order: 5 },
  { id: 'dso', label: 'DSO', visible: true, order: 6 },
  { id: 'risk', label: 'Risk', visible: true, order: 7 },
  { id: 'lastPayment', label: 'Last Payment', visible: true, order: 8 },
  { id: 'collectionStrategy', label: 'Collection Strategy', visible: true, order: 9 },
  { id: 'contact', label: 'Contact Email', visible: false, order: 10 },
  { id: 'phone', label: 'Phone', visible: false, order: 11 },
  { id: 'address', label: 'Address', visible: false, order: 12 },
  { id: 'paymentTerms', label: 'Payment Terms', visible: false, order: 13 },
  { id: 'creditController', label: 'Credit Controller', visible: false, order: 14 },
  { id: 'accountManager', label: 'Account Manager', visible: false, order: 15 },
  { id: 'availableCredit', label: 'Available Credit', visible: false, order: 16 },
  { id: 'creditUtilization', label: 'Credit Utilisation %', visible: false, order: 17 },
  { id: 'userSortKey1', label: 'User Sort Key 1', visible: false, order: 18 },
  { id: 'userSortKey2', label: 'User Sort Key 2', visible: false, order: 19 },
  { id: 'userSortKey3', label: 'User Sort Key 3', visible: false, order: 20 },
  { id: 'userSortKey4', label: 'User Sort Key 4', visible: false, order: 21 },
  { id: 'status', label: 'Status', visible: false, order: 22 },
  { id: 'onStop', label: 'On Stop', visible: false, order: 23 },
];

export function CustomersPage() {
  return (
    <ExportProvider>
      <CustomersPageInner />
    </ExportProvider>
  );
}

function CustomersPageInner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency || 'GBP';

  // Onboarding guidance modal — show once when arriving from the onboarding wizard
  const [showGuidance, setShowGuidance] = useState(() => {
    return searchParams.get('from') === 'onboarding' && !hasSeenGuidance();
  });

  useEffect(() => {
    if (showGuidance) {
      // Clean up the URL param without triggering a re-render/navigation
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('from');
          return next;
        },
        { replace: true },
      );
    }
  }, [showGuidance, setSearchParams]);

  // Quick actions hooks
  const bulkUpdateCustomersMutation = useBulkUpdateCustomers();
  const logCustomerEmailMutation = useLogCustomerEmail();
  const logCustomerPhoneCallMutation = useLogCustomerPhoneCall();
  const { data: strategiesList = [] } = useStrategiesList({ pageSize: 200 });
  const activeStrategies = useMemo(() => strategiesList.filter((s) => s.status === 'Active'), [strategiesList]);
  const { data: riskLevelsList = [] } = useRiskLevels();
  const { data: usersList = [] } = useCompanyUsersList();
  const addCustomerNoteMutation = useAddCustomerNote();
  const { data: generalSettings } = useGeneralSettings();
  const { data: currenciesList = [] } = useCurrenciesList();
  const fetchInvoiceAttachments = useCustomerInvoiceAttachments();

  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [riskFilter, setRiskFilter] = useState('all');
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);

  // Column resize state
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  // Column reorder state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const handlePinLeft = (columnId: string) => {
    const newPinnedLeft = pinnedLeft.includes(columnId)
      ? pinnedLeft.filter((id) => id !== columnId)
      : [...pinnedLeft, columnId];
    const newPinnedRight = pinnedRight.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handlePinRight = (columnId: string) => {
    const newPinnedRight = pinnedRight.includes(columnId)
      ? pinnedRight.filter((id) => id !== columnId)
      : [...pinnedRight, columnId];
    const newPinnedLeft = pinnedLeft.filter((id) => id !== columnId);
    setPinned(newPinnedLeft, newPinnedRight);
  };

  const handleResetColumnWidths = () => resetColumnWidths();
  const handleResetColumnOrder = () => resetColumnOrder();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Dialog states
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [sendEmailDialog, setSendEmailDialog] = useState(false);
  const [logCallDialog, setLogCallDialog] = useState(false);
  const [addCustomerDialog, setAddCustomerDialog] = useState(false);
  const [assignStrategyDialog, setAssignStrategyDialog] = useState(false);
  const [assignRiskLevelDialog, setAssignRiskLevelDialog] = useState(false);
  const [assignCreditControllerDialog, setAssignCreditControllerDialog] = useState(false);
  const [assignAccountManagerDialog, setAssignAccountManagerDialog] = useState(false);
  const [sendStatementsOptionsDialog, setSendStatementsOptionsDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [addNoteDialog, setAddNoteDialog] = useState(false);

  // Form states
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachInvoiceCopies, setAttachInvoiceCopies] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [callOutcome, setCallOutcome] = useState('');
  const [callDuration, setCallDuration] = useState('');
  const [callDirection, setCallDirection] = useState('outbound');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Add customer form
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    accountNumber: '',
    contact: '',
    phone: '',
    address: '',
    currency: 'GBP' as 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD',
    creditLimit: '',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  });

  // Payment expansion state
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Advanced filter states
  const defaultAdvancedFilters = useMemo(
    () => ({
      customers: [] as string[],
      currencies: [] as string[],
      paymentTerms: [] as string[],
      collectionStrategies: [] as string[],
      creditControllers: [] as string[],
      accountManagers: [] as string[],
      riskLevels: [] as string[],
      userSortKey1: [] as string[],
      userSortKey2: [] as string[],
      userSortKey3: [] as string[],
      userSortKey4: [] as string[],
      outstandingMin: null as number | null,
      outstandingMax: null as number | null,
      overdueMin: null as number | null,
      overdueMax: null as number | null,
      creditLimitMin: null as number | null,
      creditLimitMax: null as number | null,
      dsoMin: null as number | null,
      dsoMax: null as number | null,
      daysOverdueMin: null as number | null,
      daysOverdueMax: null as number | null,
      lastPaymentFrom: '' as string,
      lastPaymentTo: '' as string,
      outstandingBalanceNotZero: false,
      status: 'all' as string,
      onStop: 'all' as string,
    }),
    [],
  );
  type AdvancedFilters = typeof defaultAdvancedFilters;

  // Default filter preset: Outstanding Balance ≠ 0
  const customerDefaultFilters = useMemo(
    (): AdvancedFilters => ({
      ...defaultAdvancedFilters,
      outstandingBalanceNotZero: true,
      status: 'Active',
    }),
    [defaultAdvancedFilters],
  );

  // Migrate saved presets: legacy range values were stored as strings, now number | null.
  // Coerce them (and default any absent new fields) so older presets load correctly.
  const coercePresetFilters = useCallback(
    (filters: AdvancedFilters): AdvancedFilters => ({
      ...defaultAdvancedFilters,
      ...filters,
      outstandingMin: coerceRangeValue(filters.outstandingMin),
      outstandingMax: coerceRangeValue(filters.outstandingMax),
      overdueMin: coerceRangeValue(filters.overdueMin),
      overdueMax: coerceRangeValue(filters.overdueMax),
      creditLimitMin: coerceRangeValue(filters.creditLimitMin),
      creditLimitMax: coerceRangeValue(filters.creditLimitMax),
      dsoMin: coerceRangeValue(filters.dsoMin),
      dsoMax: coerceRangeValue(filters.dsoMax),
      daysOverdueMin: coerceRangeValue(filters.daysOverdueMin),
      daysOverdueMax: coerceRangeValue(filters.daysOverdueMax),
    }),
    [defaultAdvancedFilters],
  );

  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(customerDefaultFilters);
  const [draftFilters, setDraftFilters] = useState<AdvancedFilters>(customerDefaultFilters);

  // Saved filter presets
  const { savedPresets, activePresetId, savePreset, deletePreset, applyPreset, clearActivePreset, setActivePresetId } =
    useFilterPreferences<AdvancedFilters>(UniqueTableNamesForColumnConfiguration.Customers, {
      defaultPresets: [
        {
          id: '__default_outstanding__',
          name: 'Outstanding Customers',
          filters: customerDefaultFilters,
          isDefault: true,
        },
      ],
    });
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const pendingDraftFiltersRef = useRef<AdvancedFilters | null>(null);
  const draftPresetRef = useRef<SavedFilterPreset<AdvancedFilters> | null>(null);

  // Derive effective activePresetId: clear star when filters drift from active preset
  const effectiveActivePresetId = useMemo(() => {
    if (!activePresetId) return null;
    const activePreset = savedPresets.find((x) => x.id === activePresetId);
    if (!activePreset) return null;
    return filtersMatch(coercePresetFilters(activePreset.filters), advancedFilters) ? activePresetId : null;
  }, [activePresetId, savedPresets, advancedFilters, coercePresetFilters]);

  // Track selected customer objects for the CustomerSearchMultiSelect component
  const [draftSelectedCustomerObjects, setDraftSelectedCustomerObjects] = useState<CreatePromiseFormCustomer[]>([]);
  const [appliedSelectedCustomerObjects, setAppliedSelectedCustomerObjects] = useState<CreatePromiseFormCustomer[]>([]);

  // Check if default filter is active (outstanding ≠ 0)
  const hasCustomerDefaultFilter =
    advancedFilters.outstandingBalanceNotZero === true && advancedFilters.status === 'Active';

  // Determine if any non-default filter is applied
  const hasActiveAdvancedFilters =
    JSON.stringify(advancedFilters) !== JSON.stringify(defaultAdvancedFilters) &&
    !filtersMatch(advancedFilters, customerDefaultFilters);
  const isFiltered =
    searchQuery.trim() !== '' || riskFilter !== 'all' || hasActiveAdvancedFilters || hasCustomerDefaultFilter;

  const {
    columns: customerColumns,
    visibleColumns: customerVisibleColumns,
    savePreferences: saveCustomerColumnPreferences,
    resetToDefaults: resetCustomerColumnPreferences,
    columnOrder,
    setColumnOrder,
    columnWidths,
    setColumnWidths,
    pinnedLeft,
    pinnedRight,
    setPinned,
    resetColumnOrder,
    resetColumnWidths,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Customers, DEFAULT_CUSTOMER_COLUMNS);

  const { widths: measuredColumnWidths, measureRef } = useColumnWidthObserver();

  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>(columnWidths);
  const localColumnWidthsRef = useRef(localColumnWidths);
  localColumnWidthsRef.current = localColumnWidths;

  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  const handleResizeMouseDown = (e: React.MouseEvent, columnId: string, currentWidth: number) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { columnId, startX: e.clientX, startWidth: currentWidth };
    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId: id, startX, startWidth } = resizingRef.current;
      const delta = ev.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setLocalColumnWidths((prev) => ({ ...prev, [id]: newWidth }));
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(localColumnWidthsRef.current);
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Override user sort key labels with organisation-level custom labels from settings
  const userSortKeyLabelMap: Record<string, string> = useMemo(
    () => ({
      userSortKey1: generalSettings?.categoryKeys?.userSortKey1Label ?? 'User Sort Key 1',
      userSortKey2: generalSettings?.categoryKeys?.userSortKey2Label ?? 'User Sort Key 2',
      userSortKey3: generalSettings?.categoryKeys?.userSortKey3Label ?? 'User Sort Key 3',
      userSortKey4: generalSettings?.categoryKeys?.userSortKey4Label ?? 'User Sort Key 4',
    }),
    [generalSettings],
  );

  const customerColumnsWithLabels = useMemo(
    () =>
      customerColumns.map((col) =>
        col.id in userSortKeyLabelMap ? { ...col, label: userSortKeyLabelMap[col.id] } : col,
      ),
    [customerColumns, userSortKeyLabelMap],
  );

  const customerVisibleColumnsWithLabels = useMemo(
    () =>
      customerVisibleColumns.map((col) =>
        col.id in userSortKeyLabelMap ? { ...col, label: userSortKeyLabelMap[col.id] } : col,
      ),
    [customerVisibleColumns, userSortKeyLabelMap],
  );

  const baseVisibleColumns = customerVisibleColumns.map((col) => col.id);
  // Merge: respect saved visibility but apply local drag order
  const visibleColumns =
    columnOrder.length > 0
      ? columnOrder
          .filter((id) => baseVisibleColumns.includes(id))
          .concat(baseVisibleColumns.filter((id) => !columnOrder.includes(id)))
      : baseVisibleColumns;
  const setVisibleColumns = (newCols: string[]) => setColumnOrder(newCols);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('customer');

  // Handlers for applying / clearing / opening more filters
  const handleOpenMoreFilters = useCallback(() => {
    setDraftFilters({ ...advancedFilters });
    setDraftSelectedCustomerObjects([...appliedSelectedCustomerObjects]);
    draftPresetRef.current = null;
    setMoreFiltersOpen(true);
  }, [advancedFilters, appliedSelectedCustomerObjects]);

  // Range bounds are slider-driven (number | null); the min handle can never exceed the max, so a
  // range is only ever "invalid" defensively. isRangeInvalid keeps the Apply guard type-correct.
  const draftRangeErrors = {
    outstanding: isRangeInvalid(draftFilters.outstandingMin ?? undefined, draftFilters.outstandingMax ?? undefined),
    overdue: isRangeInvalid(draftFilters.overdueMin ?? undefined, draftFilters.overdueMax ?? undefined),
    creditLimit: isRangeInvalid(draftFilters.creditLimitMin ?? undefined, draftFilters.creditLimitMax ?? undefined),
    dso: isRangeInvalid(draftFilters.dsoMin ?? undefined, draftFilters.dsoMax ?? undefined),
  };
  const hasInvalidRange = Object.values(draftRangeErrors).some(Boolean);

  const handleApplyFilters = useCallback(() => {
    if (hasInvalidRange) return;

    if (draftPresetRef.current) {
      const preset = draftPresetRef.current;
      setAdvancedFilters(coercePresetFilters(preset.filters));
      applyPreset(preset);
    } else {
      setAdvancedFilters({ ...draftFilters });
      clearActivePreset();
    }
    setAppliedSelectedCustomerObjects([...draftSelectedCustomerObjects]);
    draftPresetRef.current = null;
    setMoreFiltersOpen(false);
  }, [
    draftFilters,
    draftSelectedCustomerObjects,
    applyPreset,
    clearActivePreset,
    coercePresetFilters,
    hasInvalidRange,
  ]);

  const handleClearDraftFilters = useCallback(() => {
    setDraftFilters({ ...defaultAdvancedFilters });
    setDraftSelectedCustomerObjects([]);
    draftPresetRef.current = null;
  }, [defaultAdvancedFilters]);

  const handleCloseMoreFilters = useCallback((open: boolean) => {
    if (!open) {
      // Discard draft changes when closing without applying
      setMoreFiltersOpen(false);
    }
  }, []);

  const handlePresetClick = useCallback(
    (preset: SavedFilterPreset<AdvancedFilters>) => {
      setDraftFilters(coercePresetFilters(preset.filters));
      draftPresetRef.current = preset;
    },
    [coercePresetFilters],
  );

  // Determine which preset is visually "active" in the sheet
  const displayActivePresetId = useMemo(() => {
    if (draftPresetRef.current) {
      return filtersMatch(draftFilters, coercePresetFilters(draftPresetRef.current.filters))
        ? draftPresetRef.current.id
        : null;
    }
    if (!effectiveActivePresetId) return null;
    const appliedPreset = savedPresets.find((x) => x.id === effectiveActivePresetId);
    if (!appliedPreset) return null;
    return filtersMatch(draftFilters, coercePresetFilters(appliedPreset.filters)) ? effectiveActivePresetId : null;
  }, [draftFilters, effectiveActivePresetId, savedPresets, coercePresetFilters]);

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Preset name required');
      return;
    }
    const filtersToSave = pendingDraftFiltersRef.current ?? advancedFilters;
    try {
      const newPresetId = await savePreset(presetName, filtersToSave);
      setActivePresetId(newPresetId);
      setPresetName('');
      setSavePresetDialogOpen(false);
      pendingDraftFiltersRef.current = null;
      toast.success(`Filter preset "${presetName}" saved`);
    } catch (error) {
      console.error('Failed to save filter preset:', error);
      const message = error instanceof Error ? error.message : 'Failed to save filter preset';
      toast.error(message);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    const preset = savedPresets.find((x) => x.id === presetId);
    if (preset?.isDefault) return;
    try {
      await deletePreset(presetId);
      toast.success('Filter preset deleted');
    } catch (error) {
      console.error('Failed to delete filter preset:', error);
      toast.error('Failed to delete filter preset');
    }
  };

  // Sorting state
  type SortField =
    | 'accountNumber'
    | 'name'
    | 'contact'
    | 'currency'
    | 'outstanding'
    | 'overdue'
    | 'creditLimit'
    | 'dso'
    | 'risk'
    | 'lastPayment'
    | 'phone'
    | 'address'
    | 'paymentTerms'
    | 'collectionStrategy'
    | 'creditController'
    | 'accountManager'
    | 'userSortKey1'
    | 'userSortKey2'
    | 'userSortKey3'
    | 'userSortKey4';
  type SortDirection = 'asc' | 'desc' | null;
  const [{ column: sortField, direction: sortDirection }, setSort] = useSessionSortPreference<
    SortField | null,
    SortDirection
  >(UniqueTableNamesForColumnConfiguration.Customers, 'accountNumber', 'asc');

  // Search + lookup/multi-select filter params (extracted to keep graphqlParams under the
  // cognitive-complexity limit).
  const applyLookupParams = useCallback(
    (params: GetCustomersGraphQLParams) => {
      if (debouncedSearch) params.search = debouncedSearch;
      if (riskFilter !== 'all') params.riskLevelCode = riskFilter === FILTER_NONE ? null : riskFilter;
      if (advancedFilters.currencies.length > 0) params.currency = advancedFilters.currencies[0];
      if (advancedFilters.paymentTerms.length > 0) params.paymentTerms = advancedFilters.paymentTerms[0];
      if (advancedFilters.collectionStrategies.length > 0) {
        params.collectionStrategyIds = advancedFilters.collectionStrategies.map(toNullableNumberId);
      }
      if (advancedFilters.creditControllers.length > 0) {
        params.creditControllerIds = advancedFilters.creditControllers.map(toNullableNumberId);
      }
      if (advancedFilters.accountManagers.length > 0) {
        params.accountManagerIds = advancedFilters.accountManagers.map(toNullableNumberId);
      }
      if (advancedFilters.customers.length > 0)
        params.customerIds = advancedFilters.customers.map((id) => parseInt(id));
      if (advancedFilters.riskLevels.length > 0) params.riskLevelCodes = advancedFilters.riskLevels.map(toNullableCode);
    },
    [debouncedSearch, riskFilter, advancedFilters],
  );

  // Numeric range + last-payment-date filter params.
  const applyRangeParams = useCallback(
    (params: GetCustomersGraphQLParams) => {
      const numericRangeKeys = [
        'outstandingMin',
        'outstandingMax',
        'overdueMin',
        'overdueMax',
        'creditLimitMin',
        'creditLimitMax',
        'dsoMin',
        'dsoMax',
        'daysOverdueMin',
        'daysOverdueMax',
      ] as const;
      for (const key of numericRangeKeys) {
        const value = advancedFilters[key];
        if (value !== null) params[key] = value;
      }
      if (advancedFilters.lastPaymentFrom) params.lastPaymentFrom = advancedFilters.lastPaymentFrom;
      if (advancedFilters.lastPaymentTo) params.lastPaymentTo = advancedFilters.lastPaymentTo;
    },
    [advancedFilters],
  );

  // User sort keys + boolean/enum filter params.
  const applyMiscParams = useCallback(
    (params: GetCustomersGraphQLParams) => {
      const userSortKeys = ['userSortKey1', 'userSortKey2', 'userSortKey3', 'userSortKey4'] as const;
      for (const key of userSortKeys) {
        if (advancedFilters[key].length > 0) params[key] = advancedFilters[key];
      }
      if (advancedFilters.outstandingBalanceNotZero) params.outstandingNotZero = true;
      if (advancedFilters.status && advancedFilters.status !== 'all') params.customerStatus = advancedFilters.status;
      if (advancedFilters.onStop && advancedFilters.onStop !== 'all') params.onStop = advancedFilters.onStop;
    },
    [advancedFilters],
  );

  // Build GraphQL params from current filter/sort/page state
  const graphqlParams = useMemo((): GetCustomersGraphQLParams => {
    const params: GetCustomersGraphQLParams = {
      page: currentPage,
      pageSize,
      sortBy: sortField ?? undefined,
      sortOrder: sortDirection ?? undefined,
    };
    applyLookupParams(params);
    applyRangeParams(params);
    applyMiscParams(params);
    return params;
  }, [currentPage, pageSize, sortField, sortDirection, applyLookupParams, applyRangeParams, applyMiscParams]);

  // Fetch customers data using GraphQL (server-side filtering, sorting, pagination)
  const {
    customers,
    kpis,
    currencyAggregation,
    pagination: graphqlPagination,
    isLoading: loading,
  } = useCustomersPageDataGraphQL(graphqlParams);

  // When exactly one currency is selected in the filter, metric cards should show totals in that currency.
  // Uses the aggregation-by-currency API: when filtered to one currency there is a single aggregation row
  // whose currency-native values are used; otherwise we fall back to the home/base totals.
  const activeCurrencyFilter = advancedFilters.currencies.length === 1 ? advancedFilters.currencies[0] : baseCurrency;
  const isSingleCurrencyFiltered = activeCurrencyFilter !== baseCurrency;
  const singleCurrencyRow =
    isSingleCurrencyFiltered && currencyAggregation.length === 1 ? currencyAggregation[0] : null;
  const kpiOutstanding = singleCurrencyRow ? singleCurrencyRow.outstandingCustomer : kpis.totalOutstanding;
  const kpiOverdue = singleCurrencyRow ? singleCurrencyRow.overdueCustomer : kpis.totalOverdue;

  const currencyOptions = (currenciesList ?? []).map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }));

  // Derive dropdown options from the current customers list
  const filterOptions = useMemo(() => {
    const paymentTerms = [...new Set(customers.map((c) => c.paymentTerms))].sort();

    const collectionStrategies = activeStrategies.map((s) => ({
      id: s.id,
      name: s.name,
    }));

    const creditControllers = (usersList ?? [])
      .map((u) => ({
        id: String(u.id),
        name: `${u.firstName} ${u.lastName}`.trim(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const accountManagers = (usersList ?? [])
      .map((u) => ({
        id: String(u.id),
        name: `${u.firstName} ${u.lastName}`.trim(),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const userSortKey1Values = [
      ...new Set(customers.map((c) => c.userSortKey1).filter((v): v is string => Boolean(v))),
    ].sort((a, b) => a.localeCompare(b));
    const userSortKey2Values = [
      ...new Set(customers.map((c) => c.userSortKey2).filter((v): v is string => Boolean(v))),
    ].sort((a, b) => a.localeCompare(b));
    const userSortKey3Values = [
      ...new Set(customers.map((c) => c.userSortKey3).filter((v): v is string => Boolean(v))),
    ].sort((a, b) => a.localeCompare(b));
    const userSortKey4Values = [
      ...new Set(customers.map((c) => c.userSortKey4).filter((v): v is string => Boolean(v))),
    ].sort((a, b) => a.localeCompare(b));

    // Use organisation-level custom labels from settings
    const userSortKey1Label = generalSettings?.categoryKeys?.userSortKey1Label ?? 'User Sort Key 1';
    const userSortKey2Label = generalSettings?.categoryKeys?.userSortKey2Label ?? 'User Sort Key 2';
    const userSortKey3Label = generalSettings?.categoryKeys?.userSortKey3Label ?? 'User Sort Key 3';
    const userSortKey4Label = generalSettings?.categoryKeys?.userSortKey4Label ?? 'User Sort Key 4';

    return {
      paymentTerms,
      collectionStrategies,
      creditControllers,
      accountManagers,
      userSortKey1Values,
      userSortKey2Values,
      userSortKey3Values,
      userSortKey4Values,
      userSortKey1Label,
      userSortKey2Label,
      userSortKey3Label,
      userSortKey4Label,
    };
  }, [activeStrategies, customers, generalSettings, usersList]);

  // Update URL params when pagination changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', currentPage.toString());
    params.set('pageSize', pageSize.toString());
    setSearchParams(params, { replace: true });
  }, [currentPage, pageSize, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, riskFilter, advancedFilters, sortField, sortDirection]);

  useEffect(() => {
    setSelectedCustomers([]);
  }, [selectedCompany?.id]);

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null (no sort)
      if (sortDirection === 'asc') {
        setSort(field, 'desc');
      } else if (sortDirection === 'desc') {
        setSort(null, null);
      }
    } else {
      // New field, start with ascending
      setSort(field, 'asc');
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4" strokeWidth={2.5} />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="h-4 w-4" strokeWidth={2.5} />;
    }
    return <ArrowDown className="h-4 w-4" strokeWidth={2.5} />;
  };

  // Server-side filtered/sorted customers from GraphQL
  const filteredCustomers = customers;

  // Server-side pagination from GraphQL
  const totalRecords = graphqlPagination.totalItems;
  const totalPages = graphqlPagination.totalPages;

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Server-side paginated customers (already paginated by GraphQL)
  const paginatedCustomers = customers;

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  // Get selected customer for floating menu
  const selectedCustomer_floatingMenu =
    selectedCustomers.length === 1 ? customers.find((c) => c.id === selectedCustomers[0]) : null;

  // Fetch contacts for selected customer (used in Log Call dialog)
  const { data: customerContacts = [] } = useCustomerContactsList(selectedCustomer_floatingMenu?.id || '0');

  // Default contact selection to primary contact when contacts load or dialog opens
  useEffect(() => {
    if (logCallDialog && customerContacts.length > 0 && !selectedContactId) {
      const primaryContact = customerContacts.find((c) => c.isPrimary);
      setSelectedContactId(primaryContact?.id || customerContacts[0]?.id || '');
    }
  }, [logCallDialog, customerContacts, selectedContactId]);

  // Get the currently selected contact's phone number
  const selectedContact = customerContacts.find((c) => c.id === selectedContactId);

  // Risk badge helper
  const getRiskBadge = (risk: string, riskLevelName?: string) => {
    if ((!risk || risk === 'none') && !riskLevelName) {
      return <span className="text-muted-foreground">None</span>;
    }
    const label = riskLevelName || getRiskLabel(risk as RiskLevelCode);
    return <Badge className={getRiskBadgeClassName(risk as RiskLevelCode)}>{label}</Badge>;
  };

  // Column rendering helpers
  const getColumnHeader = (columnId: string) => {
    const headers: Record<string, { label: string; className?: string }> = {
      accountNumber: { label: 'Account #' },
      name: { label: 'Name' },
      contact: { label: 'Contact Email' },
      currency: { label: 'Currency' },
      outstanding: {
        label: currencyMode === 'base' ? `Outstanding (${baseCurrency})` : 'Outstanding',
        className: 'text-right',
      },
      overdue: { label: currencyMode === 'base' ? `Overdue (${baseCurrency})` : 'Overdue', className: 'text-right' },
      creditLimit: {
        label: currencyMode === 'base' ? `Credit Limit (${baseCurrency})` : 'Credit Limit',
        className: 'text-right',
      },
      dso: { label: 'Performance' },
      risk: { label: 'Risk' },
      lastPayment: { label: 'Last Payment' },
      phone: { label: 'Phone' },
      address: { label: 'Address' },
      paymentTerms: { label: 'Payment Terms' },
      collectionStrategy: { label: 'Collection Strategy' },
      creditController: { label: 'Credit Controller' },
      accountManager: { label: 'Account Manager' },
      availableCredit: { label: 'Available Credit', className: 'text-right' },
      creditUtilization: { label: 'Credit Utilisation %', className: 'text-right' },
      userSortKey1: { label: filterOptions.userSortKey1Label },
      userSortKey2: { label: filterOptions.userSortKey2Label },
      userSortKey3: { label: filterOptions.userSortKey3Label },
      userSortKey4: { label: filterOptions.userSortKey4Label },
      status: { label: 'Status' },
      onStop: { label: 'On Stop' },
    };
    return headers[columnId] || { label: columnId };
  };

  const renderColumnCell = (columnId: string, customer: Customer) => {
    switch (columnId) {
      case 'accountNumber':
        return (
          <TableCell className="font-medium">
            <button onClick={() => handleViewDetails(customer)} className="text-primary hover:underline">
              {customer.accountNumber}
            </button>
          </TableCell>
        );

      case 'name':
        return (
          <TableCell>
            <div className="flex items-center gap-2">
              <button onClick={() => handleViewDetails(customer)} className="text-primary hover:underline">
                {customer.name}
              </button>
              {customer.otherCompaniesCount && customer.otherCompaniesCount > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="secondary" className="flex items-center gap-1 px-1.5 py-0.5 h-5">
                        <Building2 className="h-3 w-3" />
                        <span className="text-xs">+{customer.otherCompaniesCount}</span>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Also in {customer.otherCompaniesCount} other{' '}
                        {customer.otherCompaniesCount === 1 ? 'entity' : 'entities'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </TableCell>
        );

      case 'contact':
        return <TableCell>{customer.contact}</TableCell>;

      case 'currency':
        return (
          <TableCell>
            {customer.isAnyCurrency ? (
              <div className="flex items-center gap-1.5">
                <span>{customer.currency}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help">
                        <Globe className="h-3.5 w-3.5 text-amber-500" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[320px] text-center whitespace-normal break-words">
                      <p>
                        This account has transactions posted in multiple currencies. The default currency has been set
                        to base (GBP).
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            ) : (
              customer.currency
            )}
          </TableCell>
        );

      case 'outstanding': {
        const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCurrency;
        const displayAmount =
          currencyMode === 'base'
            ? (customer.outstandingHome ?? customer.outstanding)
            : (customer.displayOutstanding ?? customer.outstanding);
        const displayCurrency =
          currencyMode === 'base' ? baseCurrency : isNonBaseCurrency ? customer.currency : baseCurrency;
        return <TableCell className="text-right">{formatCurrency(displayAmount, displayCurrency)}</TableCell>;
      }

      case 'overdue': {
        const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCurrency;
        const overdueAmount =
          currencyMode === 'base'
            ? (customer.overdueHome ?? customer.overdue)
            : (customer.displayOverdue ?? customer.overdue);
        const overdueCurrency =
          currencyMode === 'base' ? baseCurrency : isNonBaseCurrency ? customer.currency : baseCurrency;
        return (
          <TableCell className={`text-right ${overdueAmount > 0 ? 'text-red-600' : ''}`}>
            {formatCurrency(overdueAmount, overdueCurrency)}
          </TableCell>
        );
      }

      case 'creditLimit': {
        const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCurrency;
        const displayCreditLimit =
          currencyMode === 'base'
            ? customer.creditLimit
            : isNonBaseCurrency
              ? (customer.currencyCreditLimit ?? customer.creditLimit)
              : customer.creditLimit;
        const displayCurrency =
          currencyMode === 'base' ? baseCurrency : isNonBaseCurrency ? customer.currency : baseCurrency;
        return <TableCell className="text-right">{formatCreditLimit(displayCreditLimit, displayCurrency)}</TableCell>;
      }

      case 'dso':
        return (
          <TableCell>
            <span className="text-sm">{customer.dso} days</span>
          </TableCell>
        );

      case 'risk':
        return <TableCell>{getRiskBadge(customer.risk, customer.riskLevelName)}</TableCell>;

      case 'lastPayment':
        return <TableCell>{customer.lastPayment ? formatDateDDMMYYYY(customer.lastPayment) : ''}</TableCell>;

      case 'phone':
        return <TableCell>{customer.phone}</TableCell>;

      case 'address':
        return <TableCell className="max-w-[200px] truncate">{customer.address}</TableCell>;

      case 'paymentTerms':
        return <TableCell>{customer.paymentTerms}</TableCell>;

      case 'collectionStrategy':
        return (
          <TableCell>
            {customer.collectionStrategyName ? (
              <Badge variant="outline">{customer.collectionStrategyName}</Badge>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );

      case 'creditController':
        return (
          <TableCell>
            {customer.creditControllerName ? (
              <span>{customer.creditControllerName}</span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );

      case 'accountManager':
        return (
          <TableCell>
            {customer.accountManagerName ? (
              <span>{customer.accountManagerName}</span>
            ) : (
              <span className="text-muted-foreground">None</span>
            )}
          </TableCell>
        );

      case 'availableCredit': {
        const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCurrency;
        const creditLimit =
          currencyMode === 'base'
            ? customer.creditLimit
            : isNonBaseCurrency
              ? (customer.currencyCreditLimit ?? customer.creditLimit)
              : customer.creditLimit;
        if (creditLimit === 0) {
          return (
            <TableCell className="text-right">
              <Badge className="bg-muted text-muted-foreground">No Limit</Badge>
            </TableCell>
          );
        }
        const outstanding =
          currencyMode === 'base'
            ? (customer.outstandingHome ?? customer.outstanding)
            : (customer.displayOutstanding ?? customer.outstanding);
        const availableCredit = creditLimit - outstanding;
        const displayCurrency =
          currencyMode === 'base' ? baseCurrency : isNonBaseCurrency ? customer.currency : baseCurrency;
        return <TableCell className="text-right">{formatCreditLimit(availableCredit, displayCurrency)}</TableCell>;
      }

      case 'creditUtilization': {
        const isNonBaseCurrency = !customer.isAnyCurrency && customer.currency !== baseCurrency;
        const creditLimit =
          currencyMode === 'base'
            ? customer.creditLimit
            : isNonBaseCurrency
              ? (customer.currencyCreditLimit ?? customer.creditLimit)
              : customer.creditLimit;
        if (creditLimit === 0) {
          return (
            <TableCell className="text-right">
              <Badge className="bg-muted text-muted-foreground">No Limit</Badge>
            </TableCell>
          );
        }
        const outstanding =
          currencyMode === 'base'
            ? (customer.outstandingHome ?? customer.outstanding)
            : (customer.displayOutstanding ?? customer.outstanding);
        const utilization = ((outstanding / creditLimit) * 100).toFixed(1);
        return (
          <TableCell className="text-right">
            <Badge
              variant={
                parseFloat(utilization) > 80 ? 'destructive' : parseFloat(utilization) > 60 ? 'secondary' : 'default'
              }
            >
              {utilization}%
            </Badge>
          </TableCell>
        );
      }

      case 'userSortKey1':
        return (
          <TableCell>
            {customer.userSortKey1 ? (
              <span>{customer.userSortKey1}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        );

      case 'userSortKey2':
        return (
          <TableCell>
            {customer.userSortKey2 ? (
              <span>{customer.userSortKey2}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        );

      case 'userSortKey3':
        return (
          <TableCell>
            {customer.userSortKey3 ? (
              <span>{customer.userSortKey3}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        );

      case 'userSortKey4':
        return (
          <TableCell>
            {customer.userSortKey4 ? (
              <span>{customer.userSortKey4}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        );

      case 'status':
        return (
          <TableCell>
            <Badge variant={customer.status === 'Inactive' ? 'secondary' : 'outline'}>
              {customer.status || 'Active'}
            </Badge>
          </TableCell>
        );

      case 'onStop':
        return (
          <TableCell>
            <Badge variant={customer.onStop ? 'destructive' : 'secondary'}>{customer.onStop ? 'Yes' : 'No'}</Badge>
          </TableCell>
        );

      default:
        return <TableCell>-</TableCell>;
    }
  };

  // Handle checkbox selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all customers on current page
      setSelectedCustomers(paginatedCustomers.map((c) => c.id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId: string, checked: boolean) => {
    if (checked) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    } else {
      setSelectedCustomers(selectedCustomers.filter((id) => id !== customerId));
    }
  };

  // Check if all customers on current page are selected
  const allCurrentPageSelected =
    paginatedCustomers.length > 0 && paginatedCustomers.every((customer) => selectedCustomers.includes(customer.id));

  // Handle view details
  const handleViewDetails = (customer: Customer) => {
    navigate(`/customers/${customer.id}`, { state: { customer } });
  };

  // Handle send email
  const handleSendEmail = () => {
    if (selectedCustomers.length === 0) return;

    if (selectedCustomers.length > 25) {
      toast.warning(
        `You selected ${selectedCustomers.length} customers, however you can only send emails for up to 25 customers at a time.`,
      );
      return;
    }

    // For bulk emails (multiple customers), use variable placeholders
    if (selectedCustomers.length > 1) {
      const subject = `Payment Reminder - <<accountNumber>>`;
      const body = `Dear <<customerName>>,

This is a friendly reminder regarding your outstanding balance.

Outstanding Amount: <<accountBalance>>
Overdue Amount: <<overdueAmount>>

Please contact us if you have any questions.

Best regards,
<<creditController>>`;

      setEmailSubject(subject);
      setEmailBody(body);
    }

    setSendEmailDialog(true);
  };

  // Handle log call
  const handleLogCall = () => {
    setLogCallDialog(true);
  };

  // Handle assign collection strategy
  const handleAssignStrategy = () => {
    if (selectedCustomers.length === 0) return;
    setAssignStrategyDialog(true);
  };

  // Handle add note
  const handleAddNote = () => {
    if (selectedCustomers.length !== 1) return;
    setAddNoteDialog(true);
  };

  // Handle strategy assignment confirmation
  const handleConfirmStrategyAssignment = async (strategyId: string | null) => {
    if (selectedCustomers.length === 0) return;

    const isClearing = strategyId === null;
    const strategy = isClearing ? null : strategiesList.find((s) => s.id === strategyId);
    const strategyName = isClearing ? 'None' : strategy?.name || 'Unknown Strategy';

    try {
      const { updated } = await bulkUpdateCustomersMutation.mutateAsync({
        customerIds: selectedCustomers.map((id) => parseInt(id)),
        collectionStrategyId: isClearing ? undefined : parseInt(strategyId),
        clearCollectionStrategy: isClearing,
      });

      if (updated === 0) {
        toast.error('No customers were updated');
        return;
      }

      const description = isClearing
        ? `Successfully cleared collection strategy for ${updated} customer${updated !== 1 ? 's' : ''}`
        : `Successfully assigned "${strategyName}" to ${updated} customer${updated !== 1 ? 's' : ''}`;

      toast.success('Collection Strategy Updated', { description });
      setAssignStrategyDialog(false);
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Failed to update collection strategy:', error);
      toast.error('Failed to update collection strategy');
    }
  };

  // Handle assign risk level
  const handleAssignRiskLevel = () => {
    if (selectedCustomers.length === 0) return;
    setAssignRiskLevelDialog(true);
  };

  // Handle risk level assignment confirmation
  const handleConfirmRiskLevelAssignment = async (riskLevelId: number | null) => {
    if (selectedCustomers.length === 0) return;

    const riskLevelLabel =
      riskLevelId === null ? 'None' : (riskLevelsList.find((x) => x.id === riskLevelId)?.name ?? 'Unknown');

    try {
      const { updated } = await bulkUpdateCustomersMutation.mutateAsync({
        customerIds: selectedCustomers.map((id) => parseInt(id)),
        riskLevelId: riskLevelId ?? undefined,
        clearRiskLevel: riskLevelId === null,
      });

      if (updated === 0) {
        toast.error('No customers were updated');
        return;
      }

      const description =
        riskLevelId === null
          ? `Successfully cleared risk level for ${updated} customer${updated !== 1 ? 's' : ''}`
          : `Successfully assigned "${riskLevelLabel} Risk" to ${updated} customer${updated !== 1 ? 's' : ''}`;

      toast.success('Risk Level Assigned', { description });
      setAssignRiskLevelDialog(false);
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Failed to assign risk level:', error);
      toast.error('Failed to assign risk level');
    }
  };

  // Handle assign credit controller
  const handleAssignCreditController = () => {
    if (selectedCustomers.length === 0) return;
    setAssignCreditControllerDialog(true);
  };

  // Handle credit controller assignment confirmation
  const handleConfirmCreditControllerAssignment = async (userId: string) => {
    if (selectedCustomers.length === 0) return;

    const user = usersList.find((u) => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown User';

    try {
      const { updated } = await bulkUpdateCustomersMutation.mutateAsync({
        customerIds: selectedCustomers.map((id) => parseInt(id)),
        creditControllerId: parseInt(userId),
      });

      if (updated === 0) {
        toast.error('No customers were updated');
        return;
      }

      toast.success('Credit Controller Assigned', {
        description: `Successfully assigned "${userName}" to ${updated} customer${updated !== 1 ? 's' : ''}`,
      });
      setAssignCreditControllerDialog(false);
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Failed to assign credit controller:', error);
      toast.error('Failed to assign credit controller');
    }
  };

  // Handle assign account manager
  const handleAssignAccountManager = () => {
    if (selectedCustomers.length === 0) return;
    setAssignAccountManagerDialog(true);
  };

  // Handle account manager assignment confirmation
  const handleConfirmAccountManagerAssignment = async (userId: string) => {
    if (selectedCustomers.length === 0) return;

    const user = usersList.find((u) => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : 'Unknown User';

    try {
      const { updated } = await bulkUpdateCustomersMutation.mutateAsync({
        customerIds: selectedCustomers.map((id) => parseInt(id)),
        accountManagerId: parseInt(userId),
      });

      if (updated === 0) {
        toast.error('No customers were updated');
        return;
      }

      toast.success('Account Manager Assigned', {
        description: `Successfully assigned "${userName}" to ${updated} customer${updated !== 1 ? 's' : ''}`,
      });
      setAssignAccountManagerDialog(false);
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Failed to assign account manager:', error);
      toast.error('Failed to assign account manager');
    }
  };

  // Handle send statements — opens Step 1: Statement Options modal
  const handleExportStatement = () => {
    if (selectedCustomers.length === 0) return;
    setSendStatementsOptionsDialog(true);
  };

  // Map frontend column IDs to CustomerSort enum names for the export API.
  // Extends the shared sortFieldMap with columns that are valid for export
  // but not used for sorting on the list query.
  // When currencyMode is "customer", outstanding/overdue remap to Display* variants
  // (customer currency for non-base accounts, home currency otherwise);
  // in base mode they use the Home variants.
  const exportColumnNameMap: Record<string, string> = useMemo(() => {
    const base: Record<string, string> = {
      ...sortFieldMap,
      userSortKey1: 'UserSortKey1',
      userSortKey2: 'UserSortKey2',
      userSortKey3: 'UserSortKey3',
      userSortKey4: 'UserSortKey4',
      onStop: 'OnStop',
      outstanding: 'OutstandingHome',
      overdue: 'OverdueHome',
    };
    if (currencyMode === 'customer') {
      base.outstanding = 'DisplayOutstanding';
      base.overdue = 'DisplayOverdue';
    }
    return base;
  }, [currencyMode]);

  // Build columns for server-side export based on visible columns
  const getExportColumns = useCallback((): ColumnInput[] => {
    return customerVisibleColumnsWithLabels
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [customerVisibleColumnsWithLabels, exportColumnNameMap]);

  // Build columns for server-side export with all columns
  const defaultColumnsWithLabels = useMemo(
    () =>
      DEFAULT_CUSTOMER_COLUMNS.map((col) =>
        col.id in userSortKeyLabelMap ? { ...col, label: userSortKeyLabelMap[col.id] } : col,
      ),
    [userSortKeyLabelMap],
  );

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return defaultColumnsWithLabels
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [defaultColumnsWithLabels, exportColumnNameMap]);

  // Reuse the same filter builder used by the GraphQL queries
  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGraphQLFilterVariable(graphqlParams),
    [graphqlParams],
  );

  // Server-side export hook for current view columns
  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Customers',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `customers-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hook for all columns
  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Customers',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `customers-all-columns-export-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Handle export to Excel (server-side)
  const handleExportToExcel = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle export all columns to Excel (server-side)
  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle save collection strategy
  const handleSaveCollectionStrategy = () => {
    if (!selectedCustomer) return;

    // Note: In a real implementation, this would call an API to update the customer
    // For now, just update the selected customer in the dialog
    setSelectedCustomer({ ...selectedCustomer, collectionStrategyId: editedCollectionStrategyId });

    toast.success('Collection strategy updated successfully');
  };

  // Handle add customer
  const handleAddCustomer = () => {
    // Validation
    if (
      !newCustomer.name ||
      !newCustomer.accountNumber ||
      !newCustomer.contact ||
      !newCustomer.phone ||
      !newCustomer.address ||
      !newCustomer.creditLimit
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCustomer.contact)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Account number uniqueness
    if (customers.some((c) => c.accountNumber === newCustomer.accountNumber)) {
      toast.error('Account number already exists');
      return;
    }

    // Credit limit validation
    const creditLimit = parseFloat(newCustomer.creditLimit);
    if (isNaN(creditLimit) || creditLimit <= 0) {
      toast.error('Credit limit must be a positive number');
      return;
    }

    // Note: In a real implementation, this would call an API to create the customer
    // For now, just close the dialog and show success message
    setAddCustomerDialog(false);
    setNewCustomer({
      name: '',
      accountNumber: '',
      contact: '',
      phone: '',
      address: '',
      currency: 'GBP',
      creditLimit: '',
      paymentTerms: 'Net 30',
      collectionStrategyId: '',
    });

    toast.success('Customer added successfully');
  };

  // Handle send email submit
  const handleSendEmailSubmit = async () => {
    if (selectedCustomers.length === 1 && selectedCustomer_floatingMenu) {
      try {
        await logCustomerEmailMutation.mutateAsync({
          sourceId: selectedCustomer_floatingMenu.id,
          toRecipients: [selectedCustomer_floatingMenu.contact || ''],
          subject: emailSubject,
          htmlBody: emailBody,
          isSubjectModified: false,
          isBodyModified: false,
          attachOutstandingInvoices: attachInvoiceCopies,
        });
        toast.success('Email sent successfully');
      } catch (error) {
        console.error('Failed to send email:', error);
        toast.error('Failed to send email');
      }
    } else {
      // Bulk email - send for each customer
      try {
        const emailPromises = selectedCustomers.map((custId) => {
          const cust = customers.find((c) => c.id === custId);
          return logCustomerEmailMutation.mutateAsync({
            sourceId: custId,
            toRecipients: [cust?.contact || ''],
            subject: emailSubject,
            htmlBody: emailBody,
            isSubjectModified: false,
            isBodyModified: false,
            attachOutstandingInvoices: attachInvoiceCopies,
          });
        });
        await Promise.all(emailPromises);
        toast.success(`Email sent successfully to ${selectedCustomers.length} customers`);
      } catch (error) {
        console.error('Failed to send bulk email:', error);
        toast.error('Failed to send email to some customers');
      }
    }
    setSendEmailDialog(false);
    setEmailSubject('');
    setEmailBody('');
    setAttachInvoiceCopies(false);
    setSelectedCustomers([]);
  };

  // Handle log call submit
  const handleLogCallSubmit = async () => {
    if (!callNotes || !callOutcome) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!selectedCustomer_floatingMenu) return;

    try {
      await logCustomerPhoneCallMutation.mutateAsync({
        customerId: selectedCustomer_floatingMenu.id,
        phoneNumber: selectedContact?.phone || undefined,
        durationMinutes: callDuration ? parseInt(callDuration, 10) : undefined,
        notes: callNotes,
        outcome: callOutcome,
        contactId: selectedContactId ? Number(selectedContactId) : undefined,
        direction: callDirection || undefined,
      });
      toast.success('Call logged successfully');
      setLogCallDialog(false);
      setCallNotes('');
      setCallOutcome('');
      setCallDuration('');
      setCallDirection('outbound');
      setSelectedContactId('');
      setSelectedCustomers([]);
    } catch (error) {
      console.error('Failed to log call:', error);
      toast.error('Failed to log call');
    }
  };

  const { data: ageingBandsConfig } = useAgeingBands();

  // Calculate ageing using dynamic bands
  const calculateAgeing = (customer: Customer) => {
    if (!ageingBandsConfig) return null;
    const invoices = mockInvoices[customer.id] || [];
    const today = new Date();
    const bandTotals = new Map<number, number>();

    for (const band of ageingBandsConfig) {
      bandTotals.set(band.sequence, 0);
    }

    invoices.forEach((invoice) => {
      if (invoice.status === 'paid') return;

      const dueDate = new Date(invoice.dueDate);
      const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

      for (const band of ageingBandsConfig) {
        if (daysOverdue >= band.startDays && (band.endDays === null || daysOverdue <= band.endDays)) {
          bandTotals.set(band.sequence, (bandTotals.get(band.sequence) ?? 0) + invoice.amount);
          break;
        }
      }
    });

    const bands: CustomerAgeingBandUI[] = ageingBandsConfig.map((x) => ({
      sequence: x.sequence,
      label: x.label,
      bandLabel: x.bandLabel,
      startDays: x.startDays,
      endDays: x.endDays,
      totalOutstanding: bandTotals.get(x.sequence) ?? 0,
      color: x.color,
    }));

    const total = bands.reduce((acc, x) => acc + x.totalOutstanding, 0);
    const overdue = bands.filter((x) => x.startDays > 0).reduce((acc, x) => acc + x.totalOutstanding, 0);

    return { bands, total, overdue };
  };

  // Toggle payment expansion
  const togglePaymentExpansion = (paymentId: string) => {
    if (expandedPayments.includes(paymentId)) {
      setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
    } else {
      setExpandedPayments([...expandedPayments, paymentId]);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 h-full overflow-hidden">
      {/* Onboarding guidance modal — shown once on first visit from the wizard */}
      <CustomerGuidanceModal open={showGuidance} onDismiss={() => setShowGuidance(false)} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold">Customer Accounts</h1>
          <p className="text-muted-foreground mt-2">
            View and manage all customer accounts, balances, risk levels, and collection strategies
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FeatureFeedbackCollector feature="Customer Accounts" />
          <HelpSupportButton screenId="customers" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-all hover:shadow-md border-l-4" style={{ borderLeftColor: '#14b8a6' }}>
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-teal-100 flex-shrink-0">
                  <Users className="h-4 w-4 text-teal-600" />
                </div>
              </div>
              <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Total Customers</p>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {kpis.totalCustomers}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                Active customer accounts
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-amber-100 flex-shrink-0">
                  <CurrencyIcon currencyCode={baseCurrency} className="h-4 w-4 text-amber-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Outstanding Balance</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Outstanding Balance info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {activeCurrencyFilter !== baseCurrency
                        ? `Total for ${activeCurrencyFilter} accounts only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all customer accounts.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiOutstanding, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {activeCurrencyFilter !== baseCurrency
                  ? `Total for ${activeCurrencyFilter} accounts`
                  : 'Total across all customers'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                  <CalendarClock className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>Overdue Balance</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="Overdue Balance info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      {activeCurrencyFilter !== baseCurrency
                        ? `Overdue total for ${activeCurrencyFilter} accounts only. Values shown in ${activeCurrencyFilter}.`
                        : `Total shown in base currency (${baseCurrency}). Includes all overdue invoices across all accounts.`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {formatCurrency(kpiOverdue, activeCurrencyFilter)}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                {activeCurrencyFilter !== baseCurrency
                  ? `Overdue for ${activeCurrencyFilter} accounts`
                  : 'Requires immediate attention'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="transition-all hover:shadow-md border-l-4 border-l-red-500">
          <CardContent className="pt-6">
            <div className="relative">
              <div className="absolute top-0 right-0">
                <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p style={{ fontSize: '15px', fontWeight: 400, color: '#111827' }}>High Risk Customers</p>
                <TooltipProvider delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                        aria-label="High Risk Customers info"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px] text-center">
                      Counts customers set as either High or Critical risk.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p style={{ fontSize: '22px', fontWeight: 600, color: '#111827' }} className="mt-1">
                {kpis.highRiskCount}
              </p>
              <p style={{ fontSize: '12px', fontWeight: 400, color: '#6b7280' }} className="mt-1">
                Customers requiring monitoring
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customers Table — sticky toolbar + table wrapper */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
          <div className="flex items-center gap-4">
            <span style={{ fontSize: '18px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
              {isFiltered ? '' : 'All '}Customers ({totalRecords})
            </span>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or account number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Filter by risk" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value={FILTER_NONE}>None</SelectItem>
                {riskLevelsList.map((level) => (
                  <SelectItem key={level.id} value={level.code}>
                    {level.name} Risk
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <CurrencyToggle
              value={currencyMode}
              onChange={setCurrencyMode}
              baseLabel={`Base Currency (${baseCurrency})`}
            />
            <Button variant="outline" onClick={handleOpenMoreFilters}>
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportToExcel}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setColumnConfigOpen(true)} className="h-8 w-8">
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Configure columns</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {hasCustomerDefaultFilter && !hasActiveAdvancedFilters && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200 mt-2">
              <Filter className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700">
                Default filter active: <span className="font-medium">Active Customers, Outstanding Balance &ne; 0</span>
              </span>
              <button
                onClick={() => {
                  setAdvancedFilters({
                    ...advancedFilters,
                    outstandingBalanceNotZero: false,
                    status: 'all',
                  });
                  clearActivePreset();
                  toast.info('Default filter removed');
                }}
                className="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
                title="Remove default filter"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Table card */}
        <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
          <CardContent className="p-0 flex flex-col flex-1 min-h-0">
            {/* Scrollable table area */}
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full caption-bottom text-sm [&_tr]:divide-x [&_tr]:divide-gray-100">
                <thead className="sticky top-0 z-10 bg-white shadow-sm">
                  <tr>
                    <th className="h-10 text-center font-bold text-gray-700 w-[40px] min-w-[40px] max-w-[40px] border-r border-gray-200">
                      <div className="flex items-center justify-center h-full">
                        <Checkbox checked={allCurrentPageSelected} onCheckedChange={handleSelectAll} />
                      </div>
                    </th>
                    {visibleColumns.map((columnId) => {
                      const header = getColumnHeader(columnId);
                      const isSortable = [
                        'accountNumber',
                        'name',
                        'contact',
                        'currency',
                        'outstanding',
                        'overdue',
                        'creditLimit',
                        'dso',
                        'risk',
                        'lastPayment',
                        'phone',
                        'address',
                        'paymentTerms',
                        'collectionStrategy',
                        'creditController',
                        'accountManager',
                        'userSortKey1',
                        'userSortKey2',
                        'userSortKey3',
                        'userSortKey4',
                        'onStop',
                      ].includes(columnId);
                      const colWidth = localColumnWidths[columnId];
                      const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
                      const isPinLeft = pinnedLeft.includes(columnId);
                      const isPinRight = pinnedRight.includes(columnId);
                      return (
                        <th
                          key={columnId}
                          ref={measureRef(columnId)}
                          draggable
                          onDragStart={() => setDraggedColumn(columnId)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setDragOverColumn(columnId);
                          }}
                          onDragEnd={() => {
                            setDraggedColumn(null);
                            setDragOverColumn(null);
                          }}
                          onDrop={() => {
                            if (!draggedColumn || draggedColumn === columnId) return;
                            const newCols = [...visibleColumns];
                            const fromIdx = newCols.indexOf(draggedColumn);
                            const toIdx = newCols.indexOf(columnId);
                            newCols.splice(fromIdx, 1);
                            newCols.splice(toIdx, 0, draggedColumn);
                            setVisibleColumns(newCols);
                            setDraggedColumn(null);
                            setDragOverColumn(null);
                          }}
                          style={getPinnedColumnStyle(columnId, pinnedLeft, pinnedRight, localColumnWidths, {
                            zIndex: 20,
                            unpinnedPosition: 'relative',
                            measuredWidths: measuredColumnWidths,
                            columnOrder: visibleColumns,
                          })}
                          className={`h-10 px-4 text-left font-bold text-gray-700 border-r border-gray-200 last:border-r-0 select-none bg-white
                            ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-400' : ''}
                            ${draggedColumn === columnId ? 'opacity-50' : ''}
                            ${isPinLeft || isPinRight ? 'shadow-md' : ''}
                            ${header.className || ''}`}
                        >
                          <div className="flex items-center justify-between gap-1 w-full">
                            {isSortable ? (
                              <button
                                onClick={() => handleSort(columnId as SortField)}
                                className="flex items-center gap-1 hover:text-gray-900 transition-colors font-bold"
                              >
                                <span>{header.label}</span>
                                <span className="text-gray-500">{getSortIcon(columnId as SortField)}</span>
                              </button>
                            ) : (
                              <span className="font-bold">{header.label}</span>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <button className="p-0.5 rounded hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0">
                                  <MoreVertical className="h-4 w-4 text-gray-300 hover:text-gray-500" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="z-[9999] w-48">
                                <DropdownMenuItem onClick={() => handlePinLeft(columnId)}>
                                  <Pin className="h-4 w-4 mr-2" />
                                  {isPinLeft ? 'Unpin from Left' : 'Pin to Left'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePinRight(columnId)}>
                                  <Pin className="h-4 w-4 mr-2 rotate-180" />
                                  {isPinRight ? 'Unpin from Right' : 'Pin to Right'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleResetColumnWidths}>
                                  <Maximize2 className="h-4 w-4 mr-2" />
                                  Reset Column Widths
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleResetColumnOrder}>
                                  <RotateCcw className="h-4 w-4 mr-2" />
                                  Reset Column Order
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {/* Resize handle */}
                          <div
                            className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-blue-300 opacity-0 hover:opacity-60 transition-opacity z-10"
                            onMouseDown={(e) =>
                              handleResizeMouseDown(
                                e,
                                columnId,
                                colWidth || (e.currentTarget.closest('th') as HTMLElement)?.offsetWidth || 150,
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Loading customers...</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading && filteredCustomers.length === 0 && (
                    <tr>
                      <td colSpan={visibleColumns.length + 1} className="h-64 text-center">
                        <p className="text-sm text-muted-foreground">No customers found</p>
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    filteredCustomers.length > 0 &&
                    paginatedCustomers.map((customer, index) => (
                      <TableRow key={customer.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <TableCell className="w-[40px] min-w-[40px] max-w-[40px] p-0 border-r border-gray-200">
                          <div className="flex items-center justify-center h-full py-2">
                            <Checkbox
                              checked={selectedCustomers.includes(customer.id)}
                              onCheckedChange={(checked) => handleSelectCustomer(customer.id, checked as boolean)}
                            />
                          </div>
                        </TableCell>
                        {visibleColumns.map((columnId) => {
                          const cell = renderColumnCell(columnId, customer);
                          const isPinLeft = pinnedLeft.includes(columnId);
                          const isPinRight = pinnedRight.includes(columnId);
                          if (!React.isValidElement(cell)) {
                            return <React.Fragment key={columnId}>{cell}</React.Fragment>;
                          }
                          const bodyCell = cell as React.ReactElement<React.ComponentProps<typeof TableCell>>;
                          // Only a resized column clips; until then it still grows to fit its content.
                          const sizedWidth = localColumnWidths[columnId];
                          const { title, content } = clipCell(bodyCell.props.children, sizedWidth);
                          return React.cloneElement(
                            bodyCell,
                            {
                              key: columnId,
                              title,
                              style: {
                                ...bodyCell.props.style,
                                ...(sizedWidth ? { width: `${sizedWidth}px`, minWidth: `${sizedWidth}px` } : {}),
                                ...(isPinLeft || isPinRight
                                  ? getPinnedColumnStyle(columnId, pinnedLeft, pinnedRight, localColumnWidths, {
                                      measuredWidths: measuredColumnWidths,
                                      columnOrder: visibleColumns,
                                    })
                                  : {}),
                              },
                              className: cn(
                                bodyCell.props.className,
                                (isPinLeft || isPinRight) && 'shadow-md',
                                (isPinLeft || isPinRight) && (index % 2 === 0 ? 'bg-white' : 'bg-gray-50'),
                              ),
                            },
                            content,
                          );
                        })}
                      </TableRow>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination footer — outside scroll area, always visible */}
            <div className="shrink-0 border-t px-4 py-3 flex items-center justify-between pr-16">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((page, index) =>
                    typeof page === 'string' ? (
                      <span key={`ellipsis-${index}`} className="px-1 text-muted-foreground">
                        …
                      </span>
                    ) : (
                      <Button
                        key={`page-${page}`}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handlePageChange(page as number)}
                      >
                        {page}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Details Dialog */}
      <Dialog open={viewDetailsDialog} onOpenChange={setViewDetailsDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer?.name} - {selectedCustomer?.accountNumber}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[calc(90vh-120px)]">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
                <TabsTrigger value="ageing">Ageing</TabsTrigger>
                <TabsTrigger value="invoices">Invoices</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
                <TabsTrigger value="communications">Communications</TabsTrigger>
                <TabsTrigger value="contacts">Contacts</TabsTrigger>
                <TabsTrigger value="addresses">Addresses</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Account Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Account Number</Label>
                        <p className="font-medium">{selectedCustomer?.accountNumber}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Customer Name</Label>
                        <p className="font-medium">{selectedCustomer?.name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Currency</Label>
                        <p className="font-medium">{selectedCustomer?.currency}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <Label className="text-muted-foreground">Email</Label>
                        <p className="font-medium">{selectedCustomer?.contact}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Phone</Label>
                        <p className="font-medium">{selectedCustomer?.phone}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Address</Label>
                        <p className="font-medium">{selectedCustomer?.address}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Collection Strategy</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <Label>Assigned Strategy</Label>
                        <Select value={editedCollectionStrategyId} onValueChange={setEditedCollectionStrategyId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select strategy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {collectionStrategies
                              .filter((s) => s.status === 'active')
                              .map((strategy) => (
                                <SelectItem key={strategy.id} value={strategy.id}>
                                  {strategy.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={handleSaveCollectionStrategy}
                        disabled={editedCollectionStrategyId === selectedCustomer?.collectionStrategyId}
                      >
                        Save
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Quick Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div>
                        <Label className="text-muted-foreground">Outstanding</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Overdue</Label>
                        <p className="text-2xl font-bold text-red-600">
                          {selectedCustomer && formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Performance</Label>
                        <p className="text-2xl font-bold">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Financial Tab */}
              <TabsContent value="financial" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Outstanding Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer && formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Overdue Amount</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-red-600">
                        {selectedCustomer && formatCurrency(selectedCustomer.overdue, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Credit Limit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">
                        {selectedCustomer && formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Available Credit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedCustomer && selectedCustomer.creditLimit === 0 ? (
                        <p className="text-lg font-medium text-muted-foreground">No Credit Limit Set</p>
                      ) : (
                        <p className="text-3xl font-bold text-green-600">
                          {selectedCustomer &&
                            formatCurrency(
                              selectedCustomer.creditLimit - selectedCustomer.outstanding,
                              selectedCustomer.currency,
                            )}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Terms & Performance</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div>
                        <Label className="text-muted-foreground">Payment Terms</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.paymentTerms}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Days Sales Outstanding</Label>
                        <p className="text-lg font-medium">{selectedCustomer?.dso} days</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Risk Level</Label>
                        <div className="mt-1">{selectedCustomer && getRiskBadge(selectedCustomer.risk)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Credit Utilisation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedCustomer && selectedCustomer.creditLimit === 0 ? (
                      <p className="text-lg font-medium text-muted-foreground">No Credit Limit Set</p>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm">
                          <span>
                            {selectedCustomer &&
                              formatCurrency(selectedCustomer.outstanding, selectedCustomer.currency)}{' '}
                            of{' '}
                            {selectedCustomer &&
                              formatCurrency(selectedCustomer.creditLimit, selectedCustomer.currency)}
                          </span>
                          <span className="font-medium">
                            {selectedCustomer &&
                              Math.round((selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100)}
                            %
                          </span>
                        </div>
                        <Progress
                          value={
                            selectedCustomer ? (selectedCustomer.outstanding / selectedCustomer.creditLimit) * 100 : 0
                          }
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Ageing Tab */}
              <TabsContent value="ageing" className="space-y-4">
                {selectedCustomer &&
                  (() => {
                    const ageing = calculateAgeing(selectedCustomer);
                    if (!ageing) return null;
                    const overdue = ageing.overdue;

                    const currentBand = ageing.bands.find((b) => b.startDays === 0);
                    const currentAmount = currentBand?.totalOutstanding ?? 0;

                    return (
                      <>
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0">
                            <CardTitle>Ageing Breakdown</CardTitle>
                            {overdue > 0 && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setViewDetailsDialog(false);
                                    navigate(`/customers/${selectedCustomer.id}`);
                                    toast.info('Opening customer details to send reminders');
                                  }}
                                  className="h-8"
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Send Reminders
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setViewDetailsDialog(false);
                                    navigate(`/customers/${selectedCustomer.id}?tab=collections`);
                                    toast.info('Opening Collections tab to escalate account');
                                  }}
                                  className="h-8"
                                >
                                  <AlertTriangle className="h-4 w-4 mr-2" />
                                  Escalate to Collections
                                </Button>
                              </div>
                            )}
                          </CardHeader>
                          <CardContent>
                            <AgeingBarChart data={ageing} currency={selectedCustomer.currency} />
                          </CardContent>
                        </Card>

                        <div className="grid gap-4 md:grid-cols-2">
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Current & Not Overdue</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-3xl font-bold text-green-600">
                                {formatCurrency(currentAmount, selectedCustomer.currency)}
                              </p>
                            </CardContent>
                          </Card>
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base">Overdue</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-3xl font-bold text-red-600">
                                {formatCurrency(overdue, selectedCustomer.currency)}
                              </p>
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    );
                  })()}
              </TabsContent>

              {/* Invoices Tab */}
              <TabsContent value="invoices" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Unpaid Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockInvoices[selectedCustomer.id] || []).map((invoice) => {
                            const today = new Date();
                            const dueDate = new Date(invoice.dueDate);
                            const daysOverdue = Math.floor(
                              (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24),
                            );

                            return (
                              <TableRow key={invoice.id}>
                                <TableCell className="font-medium">{invoice.id}</TableCell>
                                <TableCell>{formatDate(invoice.date)}</TableCell>
                                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(invoice.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>
                                  <Badge variant={invoice.status === 'overdue' ? 'destructive' : 'secondary'}>
                                    {invoice.status}
                                  </Badge>
                                </TableCell>
                                <TableCell className={`text-right ${daysOverdue > 0 ? 'text-red-600' : ''}`}>
                                  {daysOverdue > 0 ? daysOverdue : 0}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Payment History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Reference</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCustomer &&
                          (mockPayments[selectedCustomer.id] || []).map((payment) => (
                            <>
                              <TableRow key={payment.id}>
                                <TableCell>
                                  <Button variant="ghost" size="sm" onClick={() => togglePaymentExpansion(payment.id)}>
                                    {expandedPayments.includes(payment.id) ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-medium">{payment.id}</TableCell>
                                <TableCell>{formatDate(payment.date)}</TableCell>
                                <TableCell className="text-right">
                                  {formatCurrency(payment.amount, selectedCustomer.currency)}
                                </TableCell>
                                <TableCell>{payment.method}</TableCell>
                                <TableCell>{payment.reference}</TableCell>
                              </TableRow>
                              {expandedPayments.includes(payment.id) && (
                                <TableRow>
                                  <TableCell colSpan={6} className="bg-muted/50">
                                    <div className="p-4">
                                      <p className="mb-2 font-semibold">Invoice Allocations:</p>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>Invoice #</TableHead>
                                            <TableHead>Invoice Date</TableHead>
                                            <TableHead className="text-right">Allocated Amount</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {payment.allocations.map((allocation, idx) => (
                                            <TableRow key={idx}>
                                              <TableCell>{allocation.invoiceId}</TableCell>
                                              <TableCell>{formatDate(allocation.invoiceDate)}</TableCell>
                                              <TableCell className="text-right">
                                                {formatCurrency(allocation.amount, selectedCustomer.currency)}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Payment Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <Label className="text-muted-foreground">Total Payments (Last 3 Months)</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer &&
                            formatCurrency(
                              (mockPayments[selectedCustomer.id] || []).reduce((sum, p) => sum + p.amount, 0),
                              selectedCustomer.currency,
                            )}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Last Payment Date</Label>
                        <p className="text-2xl font-bold">
                          {selectedCustomer && formatDate(selectedCustomer.lastPayment)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Communications Tab */}
              <TabsContent value="communications">
                {selectedCustomer && (
                  <CommunicationHistoryTab customerId={selectedCustomer.id} customerName={selectedCustomer.name} />
                )}
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts">
                {selectedCustomer && (
                  <AllContactsTab customerId={selectedCustomer.id} customerName={selectedCustomer.name} />
                )}
              </TabsContent>

              {/* Addresses Tab */}
              <TabsContent value="addresses">
                {selectedCustomer && (
                  <AddressesTab
                    customerId={selectedCustomer.id}
                    customerName={selectedCustomer.name}
                    primaryAddress={selectedCustomer.address}
                  />
                )}
              </TabsContent>
            </Tabs>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Send Email - Single Customer (2-step customer flow) */}
      {selectedCustomer_floatingMenu && selectedCustomers.length === 1 && (
        <CustomerSendEmailModal
          open={sendEmailDialog}
          onOpenChange={setSendEmailDialog}
          isBulkEmail={true}
          bulkCustomers={[
            {
              id: selectedCustomer_floatingMenu.id,
              name: selectedCustomer_floatingMenu.name,
              accountNumber: selectedCustomer_floatingMenu.accountNumber,
              contact: selectedCustomer_floatingMenu.contact || '',
              phone: selectedCustomer_floatingMenu.phone || undefined,
              outstanding: selectedCustomer_floatingMenu.outstanding,
              overdue: selectedCustomer_floatingMenu.overdue,
              creditLimit: selectedCustomer_floatingMenu.creditLimit,
              currency: selectedCustomer_floatingMenu.currency,
              primaryContactFirstName: selectedCustomer_floatingMenu.primaryContactFirstName,
              primaryContactSurname: selectedCustomer_floatingMenu.primaryContactSurname,
              creditControllerName: selectedCustomer_floatingMenu.creditControllerName,
              creditControllerEmail: selectedCustomer_floatingMenu.creditControllerEmail,
            },
          ]}
          fetchAttachments={fetchInvoiceAttachments}
          onSuccess={() => setSelectedCustomers([])}
        />
      )}

      {/* Send Email - Bulk (2-step customer flow) */}
      {selectedCustomers.length > 1 && (
        <CustomerSendEmailModal
          open={sendEmailDialog}
          onOpenChange={setSendEmailDialog}
          isBulkEmail={true}
          bulkCustomers={selectedCustomers.map((id) => {
            const c = customers.find((x) => x.id === id);
            return {
              id,
              name: c?.name ?? '',
              accountNumber: c?.accountNumber ?? '',
              contact: c?.contact ?? '',
              phone: c?.phone || undefined,
              outstanding: c?.outstanding,
              overdue: c?.overdue,
              creditLimit: c?.creditLimit,
              currency: c?.currency,
              primaryContactFirstName: c?.primaryContactFirstName,
              primaryContactSurname: c?.primaryContactSurname,
              creditControllerName: c?.creditControllerName,
              creditControllerEmail: c?.creditControllerEmail,
            };
          })}
          fetchAttachments={fetchInvoiceAttachments}
          onSuccess={() => setSelectedCustomers([])}
        />
      )}

      {/* Send Statements flow (all steps) */}
      <SendStatementsFlow
        open={sendStatementsOptionsDialog}
        onOpenChange={setSendStatementsOptionsDialog}
        selectedCustomerIds={selectedCustomers}
        customers={customers as StatementCustomer[]}
        onSuccess={() => setSelectedCustomers([])}
      />

      {/* Log Call Dialog */}
      <Dialog
        open={logCallDialog}
        onOpenChange={(open) => {
          setLogCallDialog(open);
          if (!open) setSelectedContactId('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>Record details of your call with the customer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Customer</Label>
              <Input value={selectedCustomer_floatingMenu?.name || ''} disabled />
            </div>
            <div>
              <Label>Contact</Label>
              <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  {customerContacts.map((contact) => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contactDisplayName(contact)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={selectedContact?.phone || ''} disabled />
            </div>
            <div>
              <Label>Direction</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                value={callDirection}
                onValueChange={(value) => {
                  if (value) {
                    setCallDirection(value);
                  }
                }}
                className="w-full"
              >
                <ToggleGroupItem value="outbound">Outbound</ToggleGroupItem>
                <ToggleGroupItem value="inbound">Inbound</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div>
              <Label>Call Outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payment_promised">Payment Promised</SelectItem>
                  <SelectItem value="dispute_raised">Dispute Raised</SelectItem>
                  <SelectItem value="no_answer">No Answer</SelectItem>
                  <SelectItem value="left_voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="payment_plan_agreed">Payment Plan Agreed</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={callDuration}
                onChange={(e) => setCallDuration(e.target.value)}
                placeholder="5"
              />
            </div>
            <div>
              <Label>Call Notes *</Label>
              <Textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={5}
                placeholder="Enter call notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLogCallDialog(false);
                setSelectedContactId('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleLogCallSubmit}>Save Call Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialog} onOpenChange={setAddCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer</DialogTitle>
            <DialogDescription>Create a new customer account</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={newCustomer.accountNumber}
                  onChange={(e) => setNewCustomer({ ...newCustomer, accountNumber: e.target.value })}
                  placeholder="Enter account number"
                />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={newCustomer.contact}
                  onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label>Address *</Label>
                <Textarea
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  placeholder="Enter address"
                  rows={3}
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select
                  value={newCustomer.currency}
                  onValueChange={(value: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD') =>
                    setNewCustomer({ ...newCustomer, currency: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Limit *</Label>
                <Input
                  type="number"
                  value={newCustomer.creditLimit}
                  onChange={(e) => setNewCustomer({ ...newCustomer, creditLimit: e.target.value })}
                  placeholder="Enter credit limit"
                />
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={newCustomer.paymentTerms}
                  onValueChange={(value: 'Net 15' | 'Net 30' | 'Net 45' | 'Net 60' | 'Net 90') =>
                    setNewCustomer({ ...newCustomer, paymentTerms: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                    <SelectItem value="Net 90">Net 90</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Collection Strategy (Optional)</Label>
                <Select
                  value={newCustomer.collectionStrategyId}
                  onValueChange={(value) => setNewCustomer({ ...newCustomer, collectionStrategyId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select strategy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {collectionStrategies
                      .filter((s) => s.status === 'active')
                      .map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          {strategy.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustomerDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCustomer}>Add Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* More Filters Sheet */}
      <Sheet open={moreFiltersOpen} onOpenChange={handleCloseMoreFilters}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col overflow-hidden">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
            <SheetDescription>Filter customers by multiple criteria</SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto pr-4 pl-4 mt-6">
            <div className="space-y-4 pb-4">
              {/* Saved Presets */}
              {savedPresets.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Saved Presets</Label>
                  <div className="space-y-2">
                    {savedPresets.map((preset) => (
                      <div
                        key={preset.id}
                        className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                      >
                        <button
                          onClick={() => handlePresetClick(preset)}
                          className="flex items-center gap-2 flex-1 text-left"
                        >
                          {displayActivePresetId === preset.id ? (
                            <Star className="h-4 w-4 fill-primary text-primary" />
                          ) : (
                            <StarOff className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">{preset.name}</span>
                        </button>
                        {!preset.isDefault && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePreset(preset.id)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Separator />
                </div>
              )}

              {/* Customer Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Customer</Label>
                <CustomerSearchMultiSelect
                  selectedCustomers={draftSelectedCustomerObjects}
                  onSelectionChange={(selected) => {
                    setDraftSelectedCustomerObjects(selected);
                    setDraftFilters({
                      ...draftFilters,
                      customers: selected.map((c) => c.id),
                    });
                  }}
                  placeholder="Select customers..."
                />
              </div>

              {/* Risk Level Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Risk Level</Label>
                <MultiSelect
                  options={[
                    ...riskLevelsList.map((level) => ({
                      value: level.code,
                      label: level.name,
                    })),
                    { value: FILTER_NONE, label: 'None' },
                  ]}
                  placeholder="Select risk levels..."
                  emptyMessage="No risk levels found."
                  value={draftFilters.riskLevels}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      riskLevels: values,
                    })
                  }
                />
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Status</Label>
                <Select
                  value={draftFilters.status}
                  onValueChange={(value) => setDraftFilters({ ...draftFilters, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* On Stop Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">On Stop</Label>
                <Select
                  value={draftFilters.onStop}
                  onValueChange={(value) => setDraftFilters({ ...draftFilters, onStop: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select on stop status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Currency Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Currency</Label>
                <MultiSelect
                  options={currencyOptions}
                  placeholder="Select currencies..."
                  emptyMessage="No currencies found."
                  value={draftFilters.currencies}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      currencies: values,
                    })
                  }
                />
              </div>

              {/* Payment Terms Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Payment Terms</Label>
                <MultiSelect
                  options={filterOptions.paymentTerms.map((term) => ({
                    value: term,
                    label: term,
                  }))}
                  placeholder="Select payment terms..."
                  emptyMessage="No payment terms found."
                  value={draftFilters.paymentTerms}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      paymentTerms: values,
                    })
                  }
                />
              </div>

              {/* Collection Strategy Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Collection Strategy</Label>
                <MultiSelect
                  options={[
                    { value: FILTER_NONE, label: 'None' },
                    ...filterOptions.collectionStrategies.map((strategy) => ({
                      value: strategy.id,
                      label: strategy.name,
                    })),
                  ]}
                  placeholder="Select collection strategies..."
                  emptyMessage="No strategies found."
                  value={draftFilters.collectionStrategies}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      collectionStrategies: values,
                    })
                  }
                />
              </div>

              {/* Credit Controller Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Credit Controller</Label>
                <MultiSelect
                  options={[
                    { value: FILTER_NONE, label: 'None' },
                    ...filterOptions.creditControllers.map((controller) => ({
                      value: controller.id,
                      label: controller.name,
                    })),
                  ]}
                  placeholder="Select credit controllers..."
                  emptyMessage="No credit controllers found."
                  value={draftFilters.creditControllers}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      creditControllers: values,
                    })
                  }
                />
              </div>

              {/* Account Manager Filter */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Account Manager</Label>
                <MultiSelect
                  options={[
                    { value: FILTER_NONE, label: 'None' },
                    ...filterOptions.accountManagers.map((manager) => ({
                      value: manager.id,
                      label: manager.name,
                    })),
                  ]}
                  placeholder="Select account managers..."
                  emptyMessage="No account managers found."
                  value={draftFilters.accountManagers}
                  onValueChange={(values) =>
                    setDraftFilters({
                      ...draftFilters,
                      accountManagers: values,
                    })
                  }
                />
              </div>

              {/* User Sort Key 1 Filter */}
              {filterOptions.userSortKey1Values.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">{filterOptions.userSortKey1Label}</Label>
                  <MultiSelect
                    options={filterOptions.userSortKey1Values.map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${filterOptions.userSortKey1Label}...`}
                    emptyMessage="No values found."
                    value={draftFilters.userSortKey1}
                    onValueChange={(values) => setDraftFilters({ ...draftFilters, userSortKey1: values })}
                  />
                </div>
              )}

              {/* User Sort Key 2 Filter */}
              {filterOptions.userSortKey2Values.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">{filterOptions.userSortKey2Label}</Label>
                  <MultiSelect
                    options={filterOptions.userSortKey2Values.map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${filterOptions.userSortKey2Label}...`}
                    emptyMessage="No values found."
                    value={draftFilters.userSortKey2}
                    onValueChange={(values) => setDraftFilters({ ...draftFilters, userSortKey2: values })}
                  />
                </div>
              )}

              {/* User Sort Key 3 Filter */}
              {filterOptions.userSortKey3Values.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">{filterOptions.userSortKey3Label}</Label>
                  <MultiSelect
                    options={filterOptions.userSortKey3Values.map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${filterOptions.userSortKey3Label}...`}
                    emptyMessage="No values found."
                    value={draftFilters.userSortKey3}
                    onValueChange={(values) => setDraftFilters({ ...draftFilters, userSortKey3: values })}
                  />
                </div>
              )}

              {/* User Sort Key 4 Filter */}
              {filterOptions.userSortKey4Values.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">{filterOptions.userSortKey4Label}</Label>
                  <MultiSelect
                    options={filterOptions.userSortKey4Values.map((v) => ({ value: v, label: v }))}
                    placeholder={`Select ${filterOptions.userSortKey4Label}...`}
                    emptyMessage="No values found."
                    value={draftFilters.userSortKey4}
                    onValueChange={(values) => setDraftFilters({ ...draftFilters, userSortKey4: values })}
                  />
                </div>
              )}

              <Separator />

              {/* Outstanding Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Outstanding Balance</Label>
                <div className="space-y-3">
                  <div className="px-2">
                    <Slider
                      min={0}
                      max={500000}
                      step={10000}
                      value={[draftFilters.outstandingMin ?? 0, draftFilters.outstandingMax ?? 500000]}
                      onValueChange={([min, max]) =>
                        setDraftFilters({ ...draftFilters, outstandingMin: min, outstandingMax: max })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatCurrency(draftFilters.outstandingMin ?? 0, baseCurrency)}</span>
                    <span>{formatCurrency(draftFilters.outstandingMax ?? 500000, baseCurrency)}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Overdue Balance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Overdue Balance</Label>
                <div className="space-y-3">
                  <div className="px-2">
                    <Slider
                      min={0}
                      max={500000}
                      step={10000}
                      value={[draftFilters.overdueMin ?? 0, draftFilters.overdueMax ?? 500000]}
                      onValueChange={([min, max]) =>
                        setDraftFilters({ ...draftFilters, overdueMin: min, overdueMax: max })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatCurrency(draftFilters.overdueMin ?? 0, baseCurrency)}</span>
                    <span>{formatCurrency(draftFilters.overdueMax ?? 500000, baseCurrency)}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Days Overdue Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Days Overdue</Label>
                <div className="space-y-3">
                  <div className="px-2">
                    <Slider
                      min={0}
                      max={180}
                      step={10}
                      value={[draftFilters.daysOverdueMin ?? 0, draftFilters.daysOverdueMax ?? 180]}
                      onValueChange={([min, max]) =>
                        setDraftFilters({ ...draftFilters, daysOverdueMin: min, daysOverdueMax: max })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{draftFilters.daysOverdueMin ?? 0} days</span>
                    <span>{draftFilters.daysOverdueMax ?? 180} days</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Last Payment Date Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Last Payment Date</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={draftFilters.lastPaymentFrom}
                      onChange={(e) =>
                        setDraftFilters({
                          ...draftFilters,
                          lastPaymentFrom: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={draftFilters.lastPaymentTo}
                      onChange={(e) =>
                        setDraftFilters({
                          ...draftFilters,
                          lastPaymentTo: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Credit Limit Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Credit Limit</Label>
                <div className="space-y-3">
                  <div className="px-2">
                    <Slider
                      min={0}
                      max={1000000}
                      step={10000}
                      value={[draftFilters.creditLimitMin ?? 0, draftFilters.creditLimitMax ?? 1000000]}
                      onValueChange={([min, max]) =>
                        setDraftFilters({ ...draftFilters, creditLimitMin: min, creditLimitMax: max })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{formatCurrency(draftFilters.creditLimitMin ?? 0, baseCurrency)}</span>
                    <span>{formatCurrency(draftFilters.creditLimitMax ?? 1000000, baseCurrency)}</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Performance Range */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Payment Performance</Label>
                <div className="space-y-3">
                  <div className="px-2">
                    <Slider
                      min={0}
                      max={365}
                      step={5}
                      value={[draftFilters.dsoMin ?? 0, draftFilters.dsoMax ?? 365]}
                      onValueChange={([min, max]) => setDraftFilters({ ...draftFilters, dsoMin: min, dsoMax: max })}
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{draftFilters.dsoMin ?? 0} days</span>
                    <span>{draftFilters.dsoMax ?? 365} days</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <SheetFooter className="p-6 bg-background border-t">
            <div className="flex gap-2 w-full">
              <Button variant="outline" className="flex-1" onClick={handleClearDraftFilters}>
                Clear All
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                disabled={hasInvalidRange}
                onClick={() => {
                  pendingDraftFiltersRef.current = draftFilters;
                  setSavePresetDialogOpen(true);
                }}
              >
                Save Preset
              </Button>
              <Button className="flex-1" disabled={hasInvalidRange} onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Save Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>Save your current filters as a preset for quick access later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customer-preset-name">Preset Name</Label>
              <Input
                id="customer-preset-name"
                placeholder="e.g., High Value Customers"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSavePresetDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>
              <Save className="mr-2 h-4 w-4" />
              Save Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={customerColumnsWithLabels}
        onApply={saveCustomerColumnPreferences}
        onReset={resetCustomerColumnPreferences}
      />

      {/* Floating Action Toolbar - Shown when customers are selected */}
      {selectedCustomers.length > 0 && (
        <CustomerSelectionToolbar
          baseCurrency={baseCurrency}
          selectedCount={selectedCustomers.length}
          selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
          onClearSelection={() => setSelectedCustomers([])}
          onSendEmail={handleSendEmail}
          onLogCall={handleLogCall}
          onSendStatements={handleExportStatement}
          onAssignStrategy={handleAssignStrategy}
          onAssignCreditController={handleAssignCreditController}
          onAssignAccountManager={handleAssignAccountManager}
          onExportToExcel={handleExportToExcel}
          onExportAllColumns={handleExportAllColumns}
          onAddNote={handleAddNote}
          onAssignRiskLevel={handleAssignRiskLevel}
        />
      )}

      {/* Assign Collection Strategy Dialog */}
      <AssignStrategyDialog
        open={assignStrategyDialog}
        onOpenChange={setAssignStrategyDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        strategies={activeStrategies}
        onConfirm={handleConfirmStrategyAssignment}
      />

      {/* Assign Risk Level Dialog */}
      <AssignRiskLevelDialog
        open={assignRiskLevelDialog}
        onOpenChange={setAssignRiskLevelDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        riskLevels={riskLevelsList}
        onConfirm={handleConfirmRiskLevelAssignment}
      />

      {/* Assign Credit Controller Dialog */}
      <AssignCreditControllerDialog
        open={assignCreditControllerDialog}
        onOpenChange={setAssignCreditControllerDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        users={usersList}
        onConfirm={handleConfirmCreditControllerAssignment}
      />

      {/* Assign Account Manager Dialog */}
      <AssignAccountManagerDialog
        open={assignAccountManagerDialog}
        onOpenChange={setAssignAccountManagerDialog}
        selectedCustomers={customers.filter((c) => selectedCustomers.includes(c.id))}
        users={usersList}
        onConfirm={handleConfirmAccountManagerAssignment}
      />

      {/* Add Note Modal */}
      {selectedCustomer_floatingMenu && (
        <AddNoteModal
          open={addNoteDialog}
          onOpenChange={setAddNoteDialog}
          customerName={selectedCustomer_floatingMenu.name}
          customerId={selectedCustomer_floatingMenu.id}
          onSave={async (data) => {
            try {
              await addCustomerNoteMutation.mutateAsync({
                customerId: selectedCustomer_floatingMenu.id,
                content: data.content,
                userId: currentUser.id,
                userName: currentUser.name,
              });
              toast.success('Note added successfully');
              setSelectedCustomers([]);
            } catch (error) {
              console.error('Failed to add note:', error);
              toast.error('Failed to add note');
            }
          }}
        />
      )}
    </div>
  );
}
